// pages/admin/MenuManagement.js
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import axios from 'axios';
import {
  AlertTriangle,
  Award,
  Bookmark,
  Calendar,
  ChevronRight,
  Clock,
  Code,
  Coffee,
  CreditCard,
  Database,
  Edit3,
  ExternalLink,
  Eye,
  Facebook,
  FacebookIcon,
  FileText,
  Gift,
  Globe,
  Heart,
  Home,
  Image,
  Info,
  Instagram,
  InstagramIcon,
  Layers,
  Layout,
  Linkedin,
  LinkedinIcon,
  Mail,
  Map,
  MessageCircle,
  Monitor,
  Package,
  Palette,
  PenTool,
  Phone,
  PlusCircle,
  Save,
  Settings,
  ShoppingBag,
  ShoppingCart,
  Smartphone,
  Star,
  Tag,
  Trash2,
  Truck,
  Twitter,
  User,
  Users,
  X,
  Youtube
} from 'lucide-react';
import { useEffect, useState } from 'react';

// Icon mapping
const iconOptions = [
  { value: "Home", component: Home },
  { value: "Package", component: Package },
  { value: "Mail", component: Mail },
  { value: "Users", component: Users },
  { value: "Info", component: Info },
  { value: "ExternalLink", component: ExternalLink },
  { value: "ShoppingCart", component: ShoppingCart },
  { value: "User", component: User },
  { value: "Settings", component: Settings },
  { value: "FileText", component: FileText },
  { value: "Phone", component: Phone },
  { value: "MessageCircle", component: MessageCircle },
  { value: "Calendar", component: Calendar },
  { value: "Map", component: Map },
  { value: "Bookmark", component: Bookmark },
  { value: "Award", component: Award },
  { value: "Database", component: Database },
  { value: "Layers", component: Layers },
  { value: "Coffee", component: Coffee },
  { value: "Gift", component: Gift },
  { value: "Tag", component: Tag },
  { value: "Clock", component: Clock },
  { value: "CreditCard", component: CreditCard },
  { value: "PenTool", component: PenTool },
  { value: "Truck", component: Truck },
  { value: "Image", component: Image },
  { value: "Heart", component: Heart },
  { value: "Star", component: Star },
  { value: "ShoppingBag", component: ShoppingBag },
  { value: "Linkedin", component: Linkedin },
  { value: "Twitter", component: Twitter },
  { value: "Facebook", component: Facebook },
  { value: "Instagram", component: Instagram },
  { value: "Youtube", component: Youtube }
];

// Social media platform item component for adding/editing social links
const SocialLinkItem = ({ platform, url, onUpdate, onRemove }) => {
  const [editing, setEditing] = useState(false);
  const [socialData, setSocialData] = useState({ platform, url });

  const handleSave = () => {
    onUpdate(socialData);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="border rounded p-3 mb-2 bg-white">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Platform</label>
            <select
              value={socialData.platform}
              onChange={(e) => setSocialData({...socialData, platform: e.target.value})}
              className="w-full p-2 border rounded bg-white"
            >
              <option value="facebook">Facebook</option>
              <option value="instagram">Instagram</option>
              <option value="twitter">Twitter</option>
              <option value="linkedin">LinkedIn</option>
              <option value="youtube">YouTube</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-gray-500 mb-1">URL</label>
            <input
              type="text"
              value={socialData.url}
              onChange={(e) => setSocialData({...socialData, url: e.target.value})}
              placeholder="https://..."
              className="w-full p-2 border rounded"
            />
          </div>
        </div>
        <div className="flex justify-end mt-2 space-x-2">
          <button
            onClick={handleSave}
            className="px-2 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600"
          >
            Save
          </button>
          <button
            onClick={() => setEditing(false)}
            className="px-2 py-1 bg-gray-300 text-gray-700 text-sm rounded hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  const SocialIcon = socialIconMap[platform] || Globe;

  return (
    <div className="flex items-center justify-between border rounded p-2 mb-2 bg-white">
      <div className="flex items-center">
        <SocialIcon className="w-5 h-5 mr-2 text-gray-600" />
        <div>
          <div className="font-medium capitalize">{platform}</div>
          <div className="text-xs text-gray-500 truncate max-w-xs">{url}</div>
        </div>
      </div>
      <div className="flex space-x-1">
        <button
          onClick={() => setEditing(true)}
          className="p-1 text-blue-500 hover:bg-blue-100 rounded"
        >
          <Edit3 className="w-4 h-4" />
        </button>
        <button
          onClick={onRemove}
          className="p-1 text-red-500 hover:bg-red-100 rounded"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// Social icon mapping
const socialIconMap = {
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  twitter: Twitter,
  linkedin: LinkedinIcon,
  youtube: Youtube
};

// Function to render icon based on name
const renderIcon = (iconName) => {
  const icon = iconOptions.find(icon => icon.value === iconName);
  if (icon) {
    const IconComponent = icon.component;
    return <IconComponent className="w-4 h-4 mr-1" />;
  }
  return null;
};

// Color picker component
// Color picker component
const ColorPicker = ({ label, value, onChange, className = "" }) => {
  return (
    <div className={`flex items-center ${className}`}>
      <label className="block text-sm font-medium text-gray-700 mr-2">
        {label}
      </label>
      <div className="flex items-center">
        <div 
          className="h-8 w-8 border rounded cursor-pointer"
          style={{ backgroundColor: value || "#000000" }}
          onClick={() => {
            // Focus the color input when the preview is clicked
            const colorInput = document.getElementById(`color-input-${label.replace(/\s+/g, '-').toLowerCase()}`);
            if (colorInput) colorInput.click();
          }}
        ></div>
        <input
          id={`color-input-${label.replace(/\s+/g, '-').toLowerCase()}`}
          type="color"
          value={value || "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 w-8 border rounded cursor-pointer opacity-0 absolute"
        />
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
          className="ml-2 p-1 text-sm border rounded w-20"
        />
      </div>
    </div>
  );
};

// Sortable menu item component
const SortableMenuItem = ({ item, onEdit, onDelete, depth = 0 }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: item._id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  
  return (
    <div>
      <div
        ref={setNodeRef}
        style={style}
        className={`p-3 ${depth > 0 ? 'bg-gray-100' : 'bg-gray-50'} border rounded flex justify-between items-center mb-2 ${depth > 0 ? 'ml-6 border-l-4 border-l-blue-300' : ''}`}
      >
        <div className="flex items-center">
          <div 
            {...attributes} 
            {...listeners} 
            className="cursor-move mr-2 text-gray-500"
          >
            ☰
          </div>
          <div className="flex items-center">
            {item.icon && renderIcon(item.icon)}
            <div>
              <div className="font-medium">{item.title}</div>
              <div className="text-sm text-gray-500">
                {item.path}
                {item.isExternal && <ExternalLink className="inline w-3 h-3 ml-1" />}
              </div>
              {item.parent && <div className="text-xs text-blue-500 flex items-center mt-1">
                <ChevronRight className="w-3 h-3 mr-1" /> Child item
              </div>}
              
              {/* Display responsive settings if set */}
              {(item.mobileOnly || item.desktopOnly) && (
                <div className="text-xs text-orange-500 flex items-center mt-1">
                  {item.mobileOnly && <Smartphone className="w-3 h-3 mr-1" />}
                  {item.desktopOnly && <Monitor className="w-3 h-3 mr-1" />}
                  {item.mobileOnly ? 'Mobile only' : 'Desktop only'}
                </div>
              )}
              
              {/* Display style indicator if item has custom styling */}
              {item.styles && Object.values(item.styles).some(value => value !== null) && (
                <div className="text-xs text-purple-500 flex items-center mt-1">
                  <Palette className="w-3 h-3 mr-1" /> Custom styled
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex space-x-2">
          <button 
            onClick={() => onEdit(item)}
            className="px-2 py-1 bg-blue-500 text-white rounded text-sm"
          >
            Edit
          </button>
          <button 
            onClick={() => onDelete(item._id)}
            className="px-2 py-1 bg-red-500 text-white rounded text-sm"
          >
            Delete
          </button>
        </div>
      </div>
      
      {/* Render children if they exist */}
      {item.children && item.children.length > 0 && (
        <div className="pl-4">
          {item.children.map(child => (
            <SortableMenuItem 
              key={child._id} 
              item={child} 
              onEdit={onEdit} 
              onDelete={onDelete} 
              depth={depth + 1} 
            />
          ))}
        </div>
      )}
    </div>
  );
};

// TabPanel component for styling tabs
const TabPanel = ({ children, value, index }) => {
  return (
    <div className={value === index ? 'block' : 'hidden'}>
      {children}
    </div>
  );
};

const MenuManagement = () => {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [newMenuName, setNewMenuName] = useState('');
  const [newMenuType, setNewMenuType] = useState('custom');
  const [editingItem, setEditingItem] = useState(null);
  const [newMenuItem, setNewMenuItem] = useState({
    title: '',
    path: '',
    parent: '',
    isExternal: false,
    icon: '',
    styles: {},
    mobileOnly: false,
    desktopOnly: false,
    customClasses: ''
  });
  
  // Style editing state
  const [activeTab, setActiveTab] = useState(0);
  const [previewMode, setPreviewMode] = useState(false);
  const [tempStyles, setTempStyles] = useState({});
  const [tempNavbarOptions, setTempNavbarOptions] = useState({});
  const [tempFooterOptions, setTempFooterOptions] = useState({});
  const [tempCustomCss, setTempCustomCss] = useState('');
  
  // File upload state
  const [logoFile, setLogoFile] = useState(null);
  const [footerLogoFile, setFooterLogoFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  useEffect(() => {
    fetchMenus();
  }, []);
  
  // When a menu is selected, initialize temp style states
// Replace the useEffect that initializes temp states in MenuManagement.js:
useEffect(() => {
  if (selectedMenu) {
    const currentMenu = menus.find(menu => menu._id === selectedMenu);
    if (currentMenu) {
      setTempStyles({
        ...currentMenu.styles,
        // Explicitly include border properties to ensure they're loaded
        borderWidth: currentMenu.styles?.borderWidth || null,
        borderStyle: currentMenu.styles?.borderStyle || null,
        borderColor: currentMenu.styles?.borderColor || null,
      } || {});      
      // Initialize navbar options - CRITICAL: Preserve exact values
      const navbarOptions = currentMenu.navbarOptions || {};
      
      setTempNavbarOptions({
        // Base options
        logo: navbarOptions.logo,
        logoHeight: navbarOptions.logoHeight || '60px',
        logoWidth: navbarOptions.logoWidth || 'auto',
        heightDesktop: navbarOptions.heightDesktop || '80px',
        heightMobile: navbarOptions.heightMobile || '70px',
        mobileBreakpoint: navbarOptions.mobileBreakpoint || '768px',
        containerMaxWidth: navbarOptions.containerMaxWidth || '100%',
        stickyOnScroll: navbarOptions.stickyOnScroll !== false,
        showContactInfo: navbarOptions.showContactInfo,
        contactPosition: navbarOptions.contactPosition || 'top',
        
        // Layout setting
        layout: navbarOptions.layout || 'standard',
        
        // Dynamic layout options - PRESERVE EXACT VALUES
        logoPosition: navbarOptions.logoPosition,
        menuPosition: navbarOptions.menuPosition,
        
        // Contact settings - PRESERVE BOOLEAN VALUES EXACTLY
        showContactInDynamic: navbarOptions.showContactInDynamic,
        contactPositionDynamic: navbarOptions.contactPositionDynamic,
        contactStyle: navbarOptions.contactStyle,
        
        // Social settings - PRESERVE BOOLEAN VALUES EXACTLY  
        showSocialInDynamic: navbarOptions.showSocialInDynamic,
        socialPosition: navbarOptions.socialPosition,
        socialIconSize: navbarOptions.socialIconSize,
        socialLinks: navbarOptions.socialLinks || [],
        
        // Scroll behavior
        logoScrollBehavior: navbarOptions.logoScrollBehavior,
        menuScrollBehavior: navbarOptions.menuScrollBehavior,
        
        // Responsive breakpoints
        dynamicMobileBreakpoint: navbarOptions.dynamicMobileBreakpoint,
        dynamicTabletBreakpoint: navbarOptions.dynamicTabletBreakpoint,
        dynamicDesktopBreakpoint: navbarOptions.dynamicDesktopBreakpoint,
        
        // Animation
        animationDuration: navbarOptions.animationDuration,
        animationEasing: navbarOptions.animationEasing,
        
        // Background effects - PRESERVE BOOLEAN VALUES EXACTLY
        enableBlur: navbarOptions.enableBlur,
        blurAmount: navbarOptions.blurAmount,
        enableGradient: navbarOptions.enableGradient,
        gradientValue: navbarOptions.gradientValue,
        
        // Search bar - PRESERVE BOOLEAN VALUES EXACTLY
        showSearchBar: navbarOptions.showSearchBar,
        searchPosition: navbarOptions.searchPosition,
        searchPlaceholder: navbarOptions.searchPlaceholder
      });
      
      setTempFooterOptions(currentMenu.footerOptions || {});
      setTempCustomCss(currentMenu.customCss || '');
    }
  }
}, [selectedMenu, menus]);
  
  const fetchMenus = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get('/api/admin/menus', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Sort all menu items by order before setting state
      const menusWithItems = response.data.map(menu => {
        // First sort top-level items
        const topLevelItems = menu.items.filter(item => !item.parent);
        const sortedTopLevelItems = sortMenuItemsByOrder(topLevelItems);
        
        // Sort children for each top-level item
        sortedTopLevelItems.forEach(item => {
          if (item.children && item.children.length > 0) {
            item.children = sortMenuItemsByOrder(item.children);
          }
        });
        
        // Include child items
        const childItems = menu.items.filter(item => item.parent);
        
        return {
          ...menu,
          items: [...sortedTopLevelItems, ...childItems]
        };
      });
      
      console.log('Fetched and sorted menus:', menusWithItems);
      
      setMenus(menusWithItems);
      if (menusWithItems.length > 0 && !selectedMenu) {
        setSelectedMenu(menusWithItems[0]._id);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching menus:', error);
      alert('Failed to load menus. Please try again.');
    }
  };
  
  
  const createMenu = async () => {
    if (!newMenuName) return;
    
    try {
      await axios.post('/api/admin/menus', { 
        name: newMenuName,
        type: newMenuType
      });
      setNewMenuName('');
      setNewMenuType('custom');
      fetchMenus();
    } catch (error) {
      console.error('Error creating menu:', error);
      alert('Failed to create menu. Please try again.');
    }
  };
  
  // Delete entire menu
  const deleteMenu = async () => {
    if (!selectedMenu) return;
    
    if (!window.confirm('Are you sure you want to delete this entire menu? This will delete all items in the menu and cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`/api/admin/menus/${selectedMenu}`);
      fetchMenus();
      setSelectedMenu(null);
    } catch (error) {
      console.error('Error deleting menu:', error);
      alert('Failed to delete menu. Please try again.');
    }
  };
  
  const addMenuItem = async () => {
    if (!newMenuItem.title || !newMenuItem.path) return;
    
    try {
      // Convert empty string parent to null
      const itemToAdd = {
        ...newMenuItem,
        parent: newMenuItem.parent === '' ? null : newMenuItem.parent
      };
      
      await axios.post(`/api/admin/menus/${selectedMenu}/items`, itemToAdd);
      setNewMenuItem({
        title: '',
        path: '',
        parent: '',
        isExternal: false,
        icon: '',
        styles: {},
        mobileOnly: false,
        desktopOnly: false,
        customClasses: ''
      });
      fetchMenus();
    } catch (error) {
      console.error('Error adding menu item:', error);
      alert('Failed to add menu item. Please try again.');
    }
  };
  
  const updateMenuItem = async () => {
    if (!editingItem || !editingItem._id) return;
    
    try {
      // Convert empty string parent to null
      const itemToUpdate = {
        ...editingItem,
        parent: editingItem.parent === '' ? null : editingItem.parent
      };
      
      await axios.put(`/api/admin/menus/${selectedMenu}/items/${editingItem._id}`, itemToUpdate);
      setEditingItem(null);
      fetchMenus();
    } catch (error) {
      console.error('Error updating menu item:', error);
      alert('Failed to update menu item. Please try again.');
    }
  };
  
  // Update menu styles and options
  // Replace the updateMenuStyles function in MenuManagement.js with this corrected version:

  const updateMenuStyles = async () => {
    if (!selectedMenu) return;
    
    try {
      const currentMenu = menus.find(menu => menu._id === selectedMenu);
      if (!currentMenu) return;
      
      // Create complete navbar options object with preserved values
      const completeNavbarOptions = {
        // Base navbar options
        logo: tempNavbarOptions.logo,
        logoHeight: tempNavbarOptions.logoHeight || '60px',
        logoWidth: tempNavbarOptions.logoWidth || 'auto',
        heightDesktop: tempNavbarOptions.heightDesktop || '80px',
        heightMobile: tempNavbarOptions.heightMobile || '70px',
        mobileBreakpoint: tempNavbarOptions.mobileBreakpoint || '768px',
        containerMaxWidth: tempNavbarOptions.containerMaxWidth || '100%',
        stickyOnScroll: tempNavbarOptions.stickyOnScroll !== false,
        showContactInfo: tempNavbarOptions.showContactInfo,
        contactPosition: tempNavbarOptions.contactPosition || 'top',
        
        // Layout setting
        layout: tempNavbarOptions.layout || 'standard',
        
        // Dynamic layout specific options - PRESERVE EXACT VALUES
        logoPosition: tempNavbarOptions.logoPosition || 'left',
        menuPosition: tempNavbarOptions.menuPosition || 'center',
        
        // Contact information settings - PRESERVE EXACT BOOLEAN VALUES
        showContactInDynamic: tempNavbarOptions.showContactInDynamic === true,
        contactPositionDynamic: tempNavbarOptions.contactPositionDynamic || 'top-right',
        contactStyle: tempNavbarOptions.contactStyle || 'both',
        
        // Social media settings - PRESERVE EXACT BOOLEAN VALUES
        showSocialInDynamic: tempNavbarOptions.showSocialInDynamic === true,
        socialPosition: tempNavbarOptions.socialPosition || 'top-right',
        socialIconSize: tempNavbarOptions.socialIconSize || 'medium',
        socialLinks: tempNavbarOptions.socialLinks || [],
        
        // Scroll behavior settings
        logoScrollBehavior: tempNavbarOptions.logoScrollBehavior || 'shrink',
        menuScrollBehavior: tempNavbarOptions.menuScrollBehavior || 'shrink',
        
        // Responsive breakpoints
        dynamicMobileBreakpoint: tempNavbarOptions.dynamicMobileBreakpoint || '768px',
        dynamicTabletBreakpoint: tempNavbarOptions.dynamicTabletBreakpoint || '1024px',
        dynamicDesktopBreakpoint: tempNavbarOptions.dynamicDesktopBreakpoint || '1280px',
        
        // Animation settings
        animationDuration: tempNavbarOptions.animationDuration || '300ms',
        animationEasing: tempNavbarOptions.animationEasing || 'ease',
        
        // Background effects - PRESERVE EXACT BOOLEAN VALUES
        enableBlur: tempNavbarOptions.enableBlur === true,
        blurAmount: tempNavbarOptions.blurAmount || '8px',
        enableGradient: tempNavbarOptions.enableGradient === true,
        gradientValue: tempNavbarOptions.gradientValue || 'linear-gradient(90deg, #1F2937, #374151)',
        
        // Search bar settings - PRESERVE EXACT BOOLEAN VALUES
        showSearchBar: tempNavbarOptions.showSearchBar === true,
        searchPosition: tempNavbarOptions.searchPosition || 'center',
        searchPlaceholder: tempNavbarOptions.searchPlaceholder || 'Search...'
      };
      const completeStyles = {
        ...tempStyles,
        // Ensure border properties are explicitly included
        borderWidth: tempStyles.borderWidth || null,
        borderStyle: tempStyles.borderStyle || null,
        borderColor: tempStyles.borderColor || null,
      };
      const updatedData = {
        styles: completeStyles,
        navbarOptions: completeNavbarOptions,
        footerOptions: tempFooterOptions,
        customCss: tempCustomCss
      };
     
      const token = localStorage.getItem("token");
      const response = await axios.put(`/api/admin/menus/${selectedMenu}`, updatedData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      console.log('Server response:', response.data);
      
      // Refresh the menus to get the updated data
      await fetchMenus();
      
      alert('Menu styling has been updated successfully!');
    } catch (error) {
      console.error('Error updating menu styles:', error);
      console.error('Error response:', error.response?.data);
      alert('Failed to update menu styles. Please try again. Check console for details.');
    }
  };
  
  // Fixed Dynamic Layout Section HTML:
  {tempNavbarOptions.layout === 'dynamic' && (
    <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
      <h4 className="text-md font-medium text-gray-800 mb-4">Dynamic Layout Options</h4>
      
      {/* Logo Position */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Logo Position</label>
          <select
            value={tempNavbarOptions.logoPosition || 'left'}
            onChange={(e) => {
              console.log('Logo position changed to:', e.target.value);
              setTempNavbarOptions({
                ...tempNavbarOptions, 
                logoPosition: e.target.value
              });
            }}
            className="w-full p-2 border rounded bg-white"
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
            <option value="hidden">Hidden</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">Current: {tempNavbarOptions.logoPosition || 'left'}</p>
        </div>
  
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Menu Position</label>
          <select
            value={tempNavbarOptions.menuPosition || 'center'}
            onChange={(e) => {
              console.log('Menu position changed to:', e.target.value);
              setTempNavbarOptions({
                ...tempNavbarOptions, 
                menuPosition: e.target.value
              });
            }}
            className="w-full p-2 border rounded bg-white"
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">Current: {tempNavbarOptions.menuPosition || 'center'}</p>
        </div>
      </div>
  
      {/* Contact Information Settings */}
      <div className="mb-4">
        <div className="flex items-center mb-2">
          <input
            type="checkbox"
            checked={tempNavbarOptions.showContactInDynamic === true}
            onChange={(e) => {
              console.log('Show contact changed to:', e.target.checked);
              setTempNavbarOptions({
                ...tempNavbarOptions, 
                showContactInDynamic: e.target.checked
              });
            }}
            className="mr-2"
          />
          <label className="block text-sm font-medium text-gray-700">Show Contact Information</label>
          <span className="ml-2 text-xs text-gray-500">
            (Current: {tempNavbarOptions.showContactInDynamic === true ? 'Enabled' : 'Disabled'})
          </span>
        </div>
        
        {tempNavbarOptions.showContactInDynamic === true && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 bg-white rounded border">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Contact Position</label>
              <select
                value={tempNavbarOptions.contactPositionDynamic || 'top-right'}
                onChange={(e) => {
                  console.log('Contact position changed to:', e.target.value);
                  setTempNavbarOptions({
                    ...tempNavbarOptions, 
                    contactPositionDynamic: e.target.value
                  });
                }}
                className="w-full p-2 border rounded bg-white"
              >
                <option value="top-left">Top Left</option>
                <option value="top-center">Top Center</option>
                <option value="top-right">Top Right</option>
                <option value="inline-left">Inline Left</option>
                <option value="inline-right">Inline Right</option>
                <option value="bottom">Bottom Bar</option>
              </select>
            </div>
  
            <div>
              <label className="block text-xs text-gray-600 mb-1">Contact Display Style</label>
              <select
                value={tempNavbarOptions.contactStyle || 'both'}
                onChange={(e) => {
                  console.log('Contact style changed to:', e.target.value);
                  setTempNavbarOptions({
                    ...tempNavbarOptions, 
                    contactStyle: e.target.value
                  });
                }}
                className="w-full p-2 border rounded bg-white"
              >
                <option value="phone-only">Phone Only</option>
                <option value="email-only">Email Only</option>
                <option value="both">Both Phone & Email</option>
                <option value="icons-only">Icons Only</option>
              </select>
            </div>
          </div>
        )}
      </div>
  
      {/* Social Media Settings */}
      <div className="mb-4">
        <div className="flex items-center mb-2">
          <input
            type="checkbox"
            checked={tempNavbarOptions.showSocialInDynamic === true}
            onChange={(e) => {
              console.log('Show social changed to:', e.target.checked);
              setTempNavbarOptions({
                ...tempNavbarOptions, 
                showSocialInDynamic: e.target.checked
              });
            }}
            className="mr-2"
          />
          <label className="block text-sm font-medium text-gray-700">Show Social Media Links</label>
          <span className="ml-2 text-xs text-gray-500">
            (Current: {tempNavbarOptions.showSocialInDynamic === true ? 'Enabled' : 'Disabled'})
          </span>
        </div>
  
        {tempNavbarOptions.showSocialInDynamic === true && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 bg-white rounded border">
            {/* Social Media Position and Settings */}
            <div>
              <label className="block text-xs text-gray-600 mb-1">Social Media Position</label>
              <select
                value={tempNavbarOptions.socialPosition || 'top-right'}
                onChange={(e) => {
                  console.log('Social position changed to:', e.target.value);
                  setTempNavbarOptions({
                    ...tempNavbarOptions, 
                    socialPosition: e.target.value
                  });
                }}
                className="w-full p-2 border rounded bg-white"
              >
                <option value="top-left">Top Left</option>
                <option value="top-center">Top Center</option>
                <option value="top-right">Top Right</option>
                <option value="inline-left">Inline Left</option>
                <option value="inline-right">Inline Right</option>
                <option value="bottom">Bottom Bar</option>
              </select>
            </div>
  
            <div>
              <label className="block text-xs text-gray-600 mb-1">Social Icons Size</label>
              <select
                value={tempNavbarOptions.socialIconSize || 'medium'}
                onChange={(e) => {
                  console.log('Social icon size changed to:', e.target.value);
                  setTempNavbarOptions({
                    ...tempNavbarOptions, 
                    socialIconSize: e.target.value
                  });
                }}
                className="w-full p-2 border rounded bg-white"
              >
                <option value="small">Small (16px)</option>
                <option value="medium">Medium (20px)</option>
                <option value="large">Large (24px)</option>
              </select>
            </div>
          </div>
        )}
      </div>
  
      {/* Debug Information - VERY IMPORTANT FOR TROUBLESHOOTING */}
      <div className="mt-4 p-3 bg-blue-50 rounded">
        <details>
          <summary className="cursor-pointer text-sm font-medium text-blue-800">Debug Info (Click to expand)</summary>
          <div className="mt-2 text-xs">
            <p><strong>Current tempNavbarOptions values:</strong></p>
            <div className="bg-white p-2 rounded mt-1 overflow-auto text-xs">
              <p><strong>logoPosition:</strong> {JSON.stringify(tempNavbarOptions.logoPosition)}</p>
              <p><strong>menuPosition:</strong> {JSON.stringify(tempNavbarOptions.menuPosition)}</p>
              <p><strong>showContactInDynamic:</strong> {JSON.stringify(tempNavbarOptions.showContactInDynamic)}</p>
              <p><strong>showSocialInDynamic:</strong> {JSON.stringify(tempNavbarOptions.showSocialInDynamic)}</p>
              <p><strong>enableBlur:</strong> {JSON.stringify(tempNavbarOptions.enableBlur)}</p>
              <p><strong>enableGradient:</strong> {JSON.stringify(tempNavbarOptions.enableGradient)}</p>
              <p><strong>showSearchBar:</strong> {JSON.stringify(tempNavbarOptions.showSearchBar)}</p>
            </div>
            <p className="mt-2"><strong>Full tempNavbarOptions:</strong></p>
            <pre className="bg-white p-2 rounded mt-1 overflow-auto text-xs max-h-40">
              {JSON.stringify(tempNavbarOptions, null, 2)}
            </pre>
          </div>
        </details>
      </div>
  
      {/* Save Button */}
      <div className="mt-4">
        <button
          onClick={updateMenuStyles}
          className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
        >
          Save Dynamic Layout Settings
        </button>
      </div>
    </div>
  )}
  // Check if an item is a descendant of another item
  const isDescendantOf = (potentialParent, item, allItems) => {
    if (potentialParent._id === item._id) {
      return true;
    }
    
    if (!potentialParent.children || potentialParent.children.length === 0) {
      return false;
    }
    
    return potentialParent.children.some(childId => {
      const childItem = allItems.find(i => i._id === childId._id || i._id === childId);
      if (!childItem) return false;
      
      if (childItem._id === item._id) {
        return true;
      }
      
      return isDescendantOf(childItem, item, allItems);
    });
  };

  const deleteMenuItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this menu item?')) return;
    
    try {
      await axios.delete(`/api/admin/menus/${selectedMenu}/items/${itemId}`);
      fetchMenus();
    } catch (error) {
      console.error('Error deleting menu item:', error);
      alert('Failed to delete menu item. Please try again.');
    }
  };
  
  // Upload logo file
  const uploadLogoFile = async (file, isFooter = false) => {
    if (!file) return null;
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', isFooter ? 'footer-logo' : 'navbar-logo');
    
    try {
      setUploading(true);
      
      const token = localStorage.getItem("token");
      const response = await axios.post('/api/menus/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      setUploading(false);
      
      if (response.data && response.data.fileUrl) {
        return response.data.fileUrl;
      } else {
        throw new Error('Invalid server response');
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      setUploading(false);
      alert('Failed to upload logo: ' + (error.response?.data?.error || error.message));
      return null;
    }
  };  
  
  // Upload and update logos
  const handleLogoUpload = async (isFooter = false) => {
    const file = isFooter ? footerLogoFile : logoFile;
    if (!file) return;
    
    const fileUrl = await uploadLogoFile(file, isFooter);
    if (!fileUrl) return;
    
    if (isFooter) {
      setTempFooterOptions({
        ...tempFooterOptions,
        logo: fileUrl
      });
      setFooterLogoFile(null);
    } else {
      setTempNavbarOptions({
        ...tempNavbarOptions,
        logo: fileUrl
      });
      setLogoFile(null);
    }
  };
  
  // Handle drag end for reordering menu items
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      const menu = menus.find(m => m._id === selectedMenu);
      if (!menu) return;
      
      const topLevelItems = menu.items.filter(item => !item.parent);
      
      const oldIndex = topLevelItems.findIndex(item => item._id === active.id);
      const newIndex = topLevelItems.findIndex(item => item._id === over.id);
      
      if (oldIndex === -1 || newIndex === -1) {
        console.warn('Cannot reorder: items not found in top-level items');
        return;
      }
      
      const reorderedTopLevelItems = arrayMove(topLevelItems, oldIndex, newIndex);
      
      // Update all item orders based on their new positions
      const orderUpdatePromises = reorderedTopLevelItems.map((item, idx) => {
        return axios.put(`/api/admin/menus/${selectedMenu}/items/${item._id}/order`, {
          order: idx
        });
      });
      
      // Update local state first for immediate UI feedback
      const childItems = menu.items.filter(item => item.parent);
      const updatedItems = [...reorderedTopLevelItems, ...childItems];
      
      const updatedMenus = menus.map(m => {
        if (m._id === selectedMenu) {
          return {
            ...m,
            items: updatedItems
          };
        }
        return m;
      });
      
      setMenus(updatedMenus);
      
      try {
        // Wait for all order updates to complete
        await Promise.all(orderUpdatePromises);
        
        // Refresh data from server to ensure everything is in sync
        await fetchMenus();
      } catch (error) {
        console.error('Error updating menu item orders:', error);
        alert('Failed to save the new order. Please try again.');
        // Revert to the original state
        fetchMenus();
      }
    }
  };



  const sortMenuItemsByOrder = (items) => {
    return [...items].sort((a, b) => a.order - b.order);
  };

  
  // Function to delete a logo
  const deleteLogo = async (isFooter = false) => {
    try {
      if (!window.confirm('Are you sure you want to delete this logo?')) {
        return;
      }
      
      setUploading(true);
      
      // Remove the reference from the options
      if (isFooter) {
        setTempFooterOptions({
          ...tempFooterOptions,
          logo: null
        });
      } else {
        setTempNavbarOptions({
          ...tempNavbarOptions,
          logo: null
        });
      }
      
      setUploading(false);
      
      // Save the changes immediately
      await updateMenuStyles();
      
    } catch (error) {
      console.error('Error deleting logo:', error);
      setUploading(false);
      alert('Failed to delete logo. Please try again.');
    }
  };
  
  const currentMenu = menus.find(menu => menu._id === selectedMenu);
  
  const getMenuTypeLabel = (type) => {
    switch (type) {
      case 'navbar': return 'Navigation Bar';
      case 'footer': return 'Footer';
      case 'sidebar': return 'Sidebar';
      case 'custom': return 'Custom Menu';
      default: return type;
    }
  };
  
  if (loading) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center">
        <img src='../images/loading.gif'/>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <h2 className="text-2xl font-semibold">Menu Management</h2>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mx-auto p-4 space-y-6 bg-gray-50 border border-gray-200 rounded-2xl">
        <div className="md:col-span-1 bg-white p-4 shadow rounded">
          <h2 className="text-xl font-semibold mb-4">Menus</h2>
          
          <div className="mb-4">
            <input
              type="text"
              value={newMenuName}
              onChange={(e) => setNewMenuName(e.target.value)}
              placeholder="New menu name"
              className="w-full p-2 border rounded mb-2"
            />
            
            <select
              value={newMenuType}
              onChange={(e) => setNewMenuType(e.target.value)}
              className="w-full p-2 border rounded mb-2 bg-white"
            >
              <option value="custom">Custom Menu</option>
              <option value="navbar">Navigation Bar</option>
              <option value="footer">Footer</option>
              <option value="sidebar">Sidebar</option>
            </select>
            
            <button 
              onClick={createMenu}
              className="w-full py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Create Menu
            </button>
          </div>
          
          <div className="space-y-2">
            {menus.map(menu => (
              <button 
                key={menu._id}
                onClick={() => setSelectedMenu(menu._id)}
                className={`w-full p-2 text-left rounded ${
                  selectedMenu === menu._id ? 'bg-blue-100 border-blue-500' : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                <div className="flex justify-between items-center">
                  <span>{menu.name}</span>
                  <span className="text-xs px-2 py-1 bg-gray-200 rounded-full">{getMenuTypeLabel(menu.type)}</span>
                </div>
              </button>
            ))}
          </div>
          <p className='text-sm text-gray-500 italic mt-4'>Create and customize menus for different sections of your website.</p>
          
          {selectedMenu && (
            <button 
              onClick={deleteMenu}
              className="w-full mt-6 py-2 bg-red-500 text-white rounded hover:bg-red-600 flex items-center justify-center"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Delete Selected Menu
            </button>
          )}
        </div>
        
        <div className="md:col-span-2 bg-white p-4 shadow rounded">
          <h2 className="text-xl font-semibold mb-4">
            {currentMenu ? `Edit ${currentMenu.name} Menu` : 'Select a menu'}
          </h2>
          
          {currentMenu && (
            <>
              {/* Tabs for different editing aspects */}
              <div className="mb-6">
                <div className="flex border-b">
                  <button
                    className={`py-2 px-4 font-medium ${activeTab === 0 ? 'text-blue-500 border-b-2 border-blue-500' : 'text-gray-500'}`}
                    onClick={() => setActiveTab(0)}
                  >
                    Menu Items
                  </button>
                  <button
                    className={`py-2 px-4 font-medium ${activeTab === 1 ? 'text-blue-500 border-b-2 border-blue-500' : 'text-gray-500'}`}
                    onClick={() => setActiveTab(1)}
                  >
                    <Palette className="w-4 h-4 inline mr-1" /> Styling
                  </button>
                  
                  {currentMenu.type === 'navbar' && (
                    <button
                      className={`py-2 px-4 font-medium ${activeTab === 2 ? 'text-blue-500 border-b-2 border-blue-500' : 'text-gray-500'}`}
                      onClick={() => setActiveTab(2)}
                    >
                      <Layout className="w-4 h-4 inline mr-1" /> Navbar Options
                    </button>
                  )}
                  
                  {currentMenu.type === 'footer' && (
                    <button
                      className={`py-2 px-4 font-medium ${activeTab === 3 ? 'text-blue-500 border-b-2 border-blue-500' : 'text-gray-500'}`}
                      onClick={() => setActiveTab(3)}
                    >
                      <Layout className="w-4 h-4 inline mr-1" /> Footer Options
                    </button>
                  )}
                  
                  <button
                    className={`py-2 px-4 font-medium ${activeTab === 4 ? 'text-blue-500 border-b-2 border-blue-500' : 'text-gray-500'}`}
                    onClick={() => setActiveTab(4)}
                  >
                    <Code className="w-4 h-4 inline mr-1" /> Custom CSS
                  </button>
                </div>
              </div>
              
              {/* Menu Items Tab */}
              <TabPanel value={activeTab} index={0}>
                {/* Add new menu item form */}
                <div className="mb-6 bg-gray-100 p-4 rounded">
                  <h3 className="font-medium mb-2">Add Menu Item</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      value={newMenuItem.title}
                      onChange={(e) => setNewMenuItem({...newMenuItem, title: e.target.value})}
                      placeholder="Title"
                      className="p-2 border rounded"
                    />
                    <input
                      type="text"
                      value={newMenuItem.path}
                      onChange={(e) => setNewMenuItem({...newMenuItem, path: e.target.value})}
                      placeholder="Path (e.g., /about)"
                      className="p-2 border rounded"
                    />
                  </div>
                  
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Parent Item</label>
                    <select
                      value={newMenuItem.parent}
                      onChange={(e) => setNewMenuItem({...newMenuItem, parent: e.target.value})}
                      className="w-full p-2 border rounded bg-white"
                    >
                      <option value="">No Parent (Top Level)</option>
                      {currentMenu.items?.map(item => (
                        <option key={item._id} value={item._id}>
                          {item.title} {item.parent ? '(Child Item)' : '(Top Level)'}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {newMenuItem.parent 
                        ? "This will be a child item that appears in a dropdown menu" 
                        : "This will be a top-level menu item"}
                    </p>
                  </div>
                  
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Icon (Optional)</label>
                      <select
                        value={newMenuItem.icon}
                        onChange={(e) => setNewMenuItem({...newMenuItem, icon: e.target.value})}
                        className="w-full p-2 border rounded bg-white"
                      >
                        <option value="">No Icon</option>
                        {iconOptions.map(icon => (
                          <option key={icon.value} value={icon.value}>{icon.value}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Link Type</label>
                      <div className="flex items-center h-10">
                        <label className="inline-flex items-center">
                          <input
                            type="checkbox"
                            checked={newMenuItem.isExternal}
                            onChange={(e) => setNewMenuItem({...newMenuItem, isExternal: e.target.checked})}
                            className="mr-2"
                          />
                          External Link (opens in new tab)
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  {/* Responsive display options */}
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Responsive Display</label>
                      <div className="flex space-x-4">
                        <label className="inline-flex items-center">
                          <input
                            type="checkbox"
                            checked={newMenuItem.mobileOnly}
                            onChange={(e) => {
                              const mobileOnly = e.target.checked;
                              setNewMenuItem({
                                ...newMenuItem, 
                                mobileOnly,
                                // Can't be both mobile-only and desktop-only
                                desktopOnly: mobileOnly ? false : newMenuItem.desktopOnly
                              });
                            }}
                            className="mr-2"
                          />
                          <Smartphone className="w-4 h-4 mr-1" /> Mobile Only
                        </label>
                        
                        <label className="inline-flex items-center">
                          <input
                            type="checkbox"
                            checked={newMenuItem.desktopOnly}
                            onChange={(e) => {
                              const desktopOnly = e.target.checked;
                              setNewMenuItem({
                                ...newMenuItem, 
                                desktopOnly,
                                // Can't be both mobile-only and desktop-only
                                mobileOnly: desktopOnly ? false : newMenuItem.mobileOnly
                              });
                            }}
                            className="mr-2"
                          />
                          <Monitor className="w-4 h-4 mr-1" /> Desktop Only
                        </label>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Custom Classes (Optional)</label>
                      <input
                        type="text"
                        value={newMenuItem.customClasses}
                        onChange={(e) => setNewMenuItem({...newMenuItem, customClasses: e.target.value})}
                        placeholder="Additional CSS classes"
                        className="w-full p-2 border rounded"
                      />
                    </div>
                  </div>
                  
                  {newMenuItem.icon && (
                    <div className="mt-4 flex items-center">
                      <span className="mr-2">Preview:</span>
                      <div className="flex items-center bg-gray-200 px-2 py-1 rounded">
                        {renderIcon(newMenuItem.icon)}
                        <span>{newMenuItem.title || 'Item Title'}</span>
                      </div>
                    </div>
                  )}
                  
                  <button 
                    onClick={addMenuItem}
                    className="mt-4 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                  >
                    Add Item
                  </button>
                </div>              
                
                {/* Edit menu item form */}
                {editingItem && (
                  <div className="mb-6 bg-yellow-50 border-yellow-200 border p-4 rounded">
                    <h3 className="font-medium mb-2">Edit Menu Item: {editingItem.title}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <input
                        type="text"
                        value={editingItem.title}
                        onChange={(e) => setEditingItem({...editingItem, title: e.target.value})}
                        placeholder="Title"
                        className="p-2 border rounded"
                      />
                      <input
                        type="text"
                        value={editingItem.path}
                        onChange={(e) => setEditingItem({...editingItem, path: e.target.value})}
                        placeholder="Path (e.g., /about)"
                        className="p-2 border rounded"
                      />
                    </div>
                    
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Parent Item</label>
                      <select
                        value={editingItem.parent || ''}
                        onChange={(e) => setEditingItem({...editingItem, parent: e.target.value || null})}
                        className="w-full p-2 border rounded bg-white"
                      >
                        <option value="">No Parent (Top Level)</option>
                        {currentMenu.items
                          ?.filter(item => item._id !== editingItem._id) // Can't be its own parent
                          .filter(item => !isDescendantOf(item, editingItem, currentMenu.items)) // Can't select own descendants
                          .map(item => (
                            <option key={item._id} value={item._id}>
                              {item.title} {item.parent ? '(Child Item)' : '(Top Level)'}
                            </option>
                          ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        {editingItem.parent 
                          ? "This will be a child item that appears in a dropdown menu" 
                          : "This will be a top-level menu item"}
                      </p>
                    </div>
                    
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Icon (Optional)</label>
                        <select
                          value={editingItem.icon || ''}
                          onChange={(e) => setEditingItem({...editingItem, icon: e.target.value})}
                          className="w-full p-2 border rounded bg-white"
                        >
                          <option value="">No Icon</option>
                          {iconOptions.map(icon => (
                            <option key={icon.value} value={icon.value}>{icon.value}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Link Type</label>
                        <div className="flex items-center h-10">
                          <label className="inline-flex items-center">
                            <input
                              type="checkbox"
                              checked={editingItem.isExternal}
                              onChange={(e) => setEditingItem({...editingItem, isExternal: e.target.checked})}
                              className="mr-2"
                            />
                            External Link (opens in new tab)
                          </label>
                        </div>
                      </div>
                    </div>
                    
                    {/* Responsive display options */}
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Responsive Display</label>
                        <div className="flex space-x-4">
                          <label className="inline-flex items-center">
                            <input
                              type="checkbox"
                              checked={editingItem.mobileOnly}
                              onChange={(e) => {
                                const mobileOnly = e.target.checked;
                                setEditingItem({
                                  ...editingItem, 
                                  mobileOnly,
                                  // Can't be both mobile-only and desktop-only
                                  desktopOnly: mobileOnly ? false : editingItem.desktopOnly
                                });
                              }}
                              className="mr-2"
                            />
                            <Smartphone className="w-4 h-4 mr-1" /> Mobile Only
                          </label>
                          
                          <label className="inline-flex items-center">
                            <input
                              type="checkbox"
                              checked={editingItem.desktopOnly}
                              onChange={(e) => {
                                const desktopOnly = e.target.checked;
                                setEditingItem({
                                  ...editingItem, 
                                  desktopOnly,
                                  // Can't be both mobile-only and desktop-only
                                  mobileOnly: desktopOnly ? false : editingItem.mobileOnly
                                });
                              }}
                              className="mr-2"
                            />
                            <Monitor className="w-4 h-4 mr-1" /> Desktop Only
                          </label>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Custom Classes (Optional)</label>
                        <input
                          type="text"
                          value={editingItem.customClasses || ''}
                          onChange={(e) => setEditingItem({...editingItem, customClasses: e.target.value})}
                          placeholder="Additional CSS classes"
                          className="w-full p-2 border rounded"
                        />
                      </div>
                    </div>
                    
                    {/* Item Styling Section */}
                    <div className="mt-4">
                      <div className="flex items-center mb-2">
                        <label className="block text-sm font-medium text-gray-700">Custom Styling</label>
                        <button 
                          type="button"
                          onClick={() => {
                            setEditingItem({
                              ...editingItem,
                              styles: editingItem.styles ? {} : {
                                backgroundColor: null,
                                textColor: null,
                                hoverTextColor: null,
                                hoverBackgroundColor: null
                              }
                            });
                          }}
                          className="ml-2 text-xs px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded"
                        >
                          {editingItem.styles && Object.keys(editingItem.styles).length > 0 
                            ? 'Clear Styles' 
                            : 'Add Custom Styling'}
                        </button>
                      </div>
                      
                      {editingItem.styles && Object.keys(editingItem.styles).length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 bg-gray-100 rounded">
                          <ColorPicker 
                            label="Text Color"
                            value={editingItem.styles.textColor}
                            onChange={(value) => setEditingItem({
                              ...editingItem,
                              styles: {...editingItem.styles, textColor: value}
                            })}
                          />
                          <ColorPicker 
                            label="Background Color"
                            value={editingItem.styles.backgroundColor}
                            onChange={(value) => setEditingItem({
                              ...editingItem,
                              styles: {...editingItem.styles, backgroundColor: value}
                            })}
                          />
                          <ColorPicker 
                            label="Hover Text Color"
                            value={editingItem.styles.hoverTextColor}
                            onChange={(value) => setEditingItem({
                              ...editingItem,
                              styles: {...editingItem.styles, hoverTextColor: value}
                            })}
                          />
                          <ColorPicker 
                            label="Hover Background"
                            value={editingItem.styles.hoverBackgroundColor}
                            onChange={(value) => setEditingItem({
                              ...editingItem,
                              styles: {...editingItem.styles, hoverBackgroundColor: value}
                            })}
                          />
                        </div>
                      )}
                    </div>
                    
                    {editingItem.icon && (
                      <div className="mt-4 flex items-center">
                        <span className="mr-2">Preview:</span>
                        <div className="flex items-center bg-gray-200 px-2 py-1 rounded">
                          {renderIcon(editingItem.icon)}
                          <span>{editingItem.title || 'Item Title'}</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Warning when changing parent status */}
                    {(editingItem.parent === null && editingItem.children?.length > 0) && (
                      <div className="mt-4 p-3 bg-red-100 text-red-700 rounded flex items-start">
                        <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium">Warning: This item has children</p>
                          <p className="text-sm">If you change this to be a child item, its existing children will be orphaned.</p>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex mt-4 space-x-2">
                      <button 
                        onClick={updateMenuItem}
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                      >
                        Update
                      </button>
                      <button 
                        onClick={() => setEditingItem(null)}
                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                      >
                        Cancel
                      </button>
                      </div>
                  </div>
                )}
                
                {/* Menu items list with drag and drop */}
                <div className="menu-items-list">
                  <h3 className="font-medium mb-2">Menu Items</h3>
                  
                  {currentMenu.items?.length > 0 ? (
                    <div>
                      {/* Only make top-level items sortable */}
                      <DndContext 
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                      <SortableContext 
                        items={currentMenu.items
                          .filter(item => !item.parent)
                          .sort((a, b) => a.order - b.order)
                          .map(item => item._id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {currentMenu.items
                          .filter(item => !item.parent)
                          .sort((a, b) => a.order - b.order)
                          .map((item) => (
                            <SortableMenuItem
                              key={item._id}
                              item={item}
                              onEdit={setEditingItem}
                              onDelete={deleteMenuItem}
                            />
                          ))}
                      </SortableContext>
                      </DndContext>
                    </div>
                  ) : (
                    <div className="text-center p-4 bg-gray-50 rounded border">
                      <p className="text-gray-500">No menu items yet. Add your first item above.</p>
                    </div>
                  )}
                </div>
              </TabPanel>
              
{/* Global Style Tab */}
<TabPanel value={activeTab} index={1}>
  <div className="bg-gray-100 p-4 rounded">
    <div className="flex justify-between mb-4">
      <h3 className="font-medium">Global Menu Styling</h3>
      <div className="flex items-center">
        <button
          onClick={() => setPreviewMode(!previewMode)}
          className="flex items-center text-sm mr-2 px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded"
        >
          {previewMode ? (
            <>
              <Edit3 className="w-3 h-3 mr-1" /> Edit Mode
            </>
          ) : (
            <>
              <Eye className="w-3 h-3 mr-1" /> Preview Mode
            </>
          )}
        </button>
        <button
          onClick={updateMenuStyles}
          className="flex items-center text-sm px-2 py-1 bg-green-500 hover:bg-green-600 text-white rounded"
        >
          <Save className="w-3 h-3 mr-1" /> Save Styles
        </button>
      </div>
    </div>
    
    {previewMode ? (
      <div className="p-4 bg-white border rounded">
        <h4 className="text-lg mb-2">Style Preview</h4>
        <div 
          className="mb-4 p-3 rounded overflow-hidden" 
          style={{
            backgroundColor: tempStyles.backgroundColor || '#1F2937',
            fontFamily: tempStyles.fontFamily,
            fontSize: tempStyles.fontSize,
            fontWeight: tempStyles.fontWeight,
            borderRadius: tempStyles.borderRadius,
            padding: tempStyles.padding,
            margin: tempStyles.margin,
            boxShadow: tempStyles.boxShadow,
            transition: tempStyles.transition,
          }}
        >
          <div className="flex space-x-4">
            {/* Sample menu items to preview styling */}
            {['Home', 'About', 'Services', 'Contact'].map((item, index) => (
              <div 
                key={index}
                className="px-3 py-2 rounded transition-all duration-200 cursor-pointer"
                style={{
                  color: tempStyles.textColor || '#D1D5DB',
                  backgroundColor: index === 1 ? 
                    (tempStyles.activeBackgroundColor || '#374151') : 'transparent',
                  border: '1px solid transparent',
                  fontFamily: tempStyles.fontFamily,
                  fontSize: tempStyles.fontSize,
                  fontWeight: tempStyles.fontWeight,
                  borderRadius: tempStyles.borderRadius,
                  transition: tempStyles.transition,
                }}
                onMouseEnter={(e) => {
                  if (index !== 1) { // Don't apply hover to "active" item
                    e.currentTarget.style.color = tempStyles.hoverTextColor || '#93C5FD';
                    e.currentTarget.style.backgroundColor = tempStyles.hoverBackgroundColor || '#374151';
                  }
                }}
                onMouseLeave={(e) => {
                  if (index !== 1) { // Don't apply hover to "active" item
                    e.currentTarget.style.color = tempStyles.textColor || '#D1D5DB';
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {item} {index === 1 && <span className="text-xs">(active)</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    ) : (
      <div className="space-y-6">
        {/* Color Settings */}
        <div>
          <h4 className="text-md font-medium text-gray-800 mb-3">Color Settings</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ColorPicker 
              label="Background Color"
              value={tempStyles.backgroundColor}
              onChange={(value) => setTempStyles({...tempStyles, backgroundColor: value})}
            />
            <ColorPicker 
              label="Text Color"
              value={tempStyles.textColor}
              onChange={(value) => setTempStyles({...tempStyles, textColor: value})}
            />
            <ColorPicker 
              label="Hover Text Color"
              value={tempStyles.hoverTextColor}
              onChange={(value) => setTempStyles({...tempStyles, hoverTextColor: value})}
            />
            <ColorPicker 
              label="Hover Background"
              value={tempStyles.hoverBackgroundColor}
              onChange={(value) => setTempStyles({...tempStyles, hoverBackgroundColor: value})}
            />
            <ColorPicker 
              label="Active Text Color"
              value={tempStyles.activeTextColor}
              onChange={(value) => setTempStyles({...tempStyles, activeTextColor: value})}
            />
            <ColorPicker 
              label="Active Background"
              value={tempStyles.activeBackgroundColor}
              onChange={(value) => setTempStyles({...tempStyles, activeBackgroundColor: value})}
            />
          </div>
        </div>

        {/* Typography Settings */}
        <div>
          <h4 className="text-md font-medium text-gray-800 mb-3">Typography</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Font Family</label>
              <select
                value={tempStyles.fontFamily || ''}
                onChange={(e) => setTempStyles({...tempStyles, fontFamily: e.target.value || null})}
                className="w-full p-2 border rounded bg-white"
              >
                <option value="">Default Font</option>
                <option value="'Inter', sans-serif">Inter</option>
                <option value="'Roboto', sans-serif">Roboto</option>
                <option value="'Open Sans', sans-serif">Open Sans</option>
                <option value="'Montserrat', sans-serif">Montserrat</option>
                <option value="'Lato', sans-serif">Lato</option>
                <option value="'Poppins', sans-serif">Poppins</option>
                <option value="'Source Sans Pro', sans-serif">Source Sans Pro</option>
                <option value="'Oswald', sans-serif">Oswald</option>
                <option value="'Roboto Condensed', sans-serif">Roboto Condensed</option>
                <option value="'Playfair Display', serif">Playfair Display</option>
                <option value="'Merriweather', serif">Merriweather</option>
                <option value="'Arial', sans-serif">Arial</option>
                <option value="'Helvetica', sans-serif">Helvetica</option>
                <option value="'Times New Roman', serif">Times New Roman</option>
                <option value="'Georgia', serif">Georgia</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Font Size</label>
              <select
                value={tempStyles.fontSize || ''}
                onChange={(e) => setTempStyles({...tempStyles, fontSize: e.target.value || null})}
                className="w-full p-2 border rounded bg-white"
              >
                <option value="">Default Size</option>
                <option value="0.75rem">Extra Small (12px)</option>
                <option value="0.875rem">Small (14px)</option>
                <option value="1rem">Medium (16px)</option>
                <option value="1.125rem">Large (18px)</option>
                <option value="1.25rem">Extra Large (20px)</option>
                <option value="1.5rem">2XL (24px)</option>
                <option value="1.875rem">3XL (30px)</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Font Weight</label>
              <select
                value={tempStyles.fontWeight || ''}
                onChange={(e) => setTempStyles({...tempStyles, fontWeight: e.target.value || null})}
                className="w-full p-2 border rounded bg-white"
              >
                <option value="">Default Weight</option>
                <option value="100">Thin (100)</option>
                <option value="200">Extra Light (200)</option>
                <option value="300">Light (300)</option>
                <option value="400">Regular (400)</option>
                <option value="500">Medium (500)</option>
                <option value="600">Semibold (600)</option>
                <option value="700">Bold (700)</option>
                <option value="800">Extra Bold (800)</option>
                <option value="900">Black (900)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Layout & Spacing Settings */}
        <div>
          <h4 className="text-md font-medium text-gray-800 mb-3">Layout & Spacing</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Border Radius</label>
              <select
                value={tempStyles.borderRadius || ''}
                onChange={(e) => setTempStyles({...tempStyles, borderRadius: e.target.value || null})}
                className="w-full p-2 border rounded bg-white"
              >
                <option value="">Default</option>
                <option value="0">Square (0px)</option>
                <option value="0.125rem">Extra Small (2px)</option>
                <option value="0.25rem">Small (4px)</option>
                <option value="0.375rem">Medium (6px)</option>
                <option value="0.5rem">Large (8px)</option>
                <option value="0.75rem">Extra Large (12px)</option>
                <option value="1rem">2XL (16px)</option>
                <option value="9999px">Pill</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Padding</label>
              <input
                type="text"
                value={tempStyles.padding || ''}
                onChange={(e) => setTempStyles({...tempStyles, padding: e.target.value || null})}
                placeholder="e.g., 0.5rem 1rem, 8px 16px"
                className="w-full p-2 border rounded"
              />
              <p className="text-xs text-gray-500 mt-1">Use CSS padding values (top right bottom left)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Margin</label>
              <input
                type="text"
                value={tempStyles.margin || ''}
                onChange={(e) => setTempStyles({...tempStyles, margin: e.target.value || null})}
                placeholder="e.g., 0 auto, 10px 5px"
                className="w-full p-2 border rounded"
              />
              <p className="text-xs text-gray-500 mt-1">Use CSS margin values (top right bottom left)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Box Shadow</label>
              <input
                type="text"
                value={tempStyles.boxShadow || ''}
                onChange={(e) => setTempStyles({...tempStyles, boxShadow: e.target.value || null})}
                placeholder="e.g., 0 4px 6px rgba(0,0,0,0.1)"
                className="w-full p-2 border rounded"
              />
              <p className="text-xs text-gray-500 mt-1">CSS box-shadow property</p>
            </div>
          </div>
        </div>

        {/* Animation & Effects */}
        <div>
          <h4 className="text-md font-medium text-gray-800 mb-3">Animation & Effects</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Transition</label>
              <select
                value={tempStyles.transition || ''}
                onChange={(e) => setTempStyles({...tempStyles, transition: e.target.value || null})}
                className="w-full p-2 border rounded bg-white"
              >
                <option value="">Default</option>
                <option value="all 0.15s ease">Fast (0.15s)</option>
                <option value="all 0.3s ease">Normal (0.3s)</option>
                <option value="all 0.5s ease">Slow (0.5s)</option>
                <option value="all 0.3s ease-in">Ease In</option>
                <option value="all 0.3s ease-out">Ease Out</option>
                <option value="all 0.3s ease-in-out">Ease In Out</option>
                <option value="all 0.3s linear">Linear</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Custom Transition</label>
              <input
                type="text"
                value={tempStyles.transition || ''}
                onChange={(e) => setTempStyles({...tempStyles, transition: e.target.value || null})}
                placeholder="e.g., all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                className="w-full p-2 border rounded"
              />
              <p className="text-xs text-gray-500 mt-1">Custom CSS transition property</p>
            </div>
          </div>
        </div>

        {/* Border Settings */}
        <div>
          <h4 className="text-md font-medium text-gray-800 mb-3">Border Settings</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Border Width</label>
              <select
                value={tempStyles.borderWidth || ''}
                onChange={(e) => setTempStyles({...tempStyles, borderWidth: e.target.value || null})}
                className="w-full p-2 border rounded bg-white"
              >
                <option value="">No Border</option>
                <option value="1px">Thin (1px)</option>
                <option value="2px">Medium (2px)</option>
                <option value="3px">Thick (3px)</option>
                <option value="4px">Extra Thick (4px)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Border Style</label>
              <select
                value={tempStyles.borderStyle || ''}
                onChange={(e) => setTempStyles({...tempStyles, borderStyle: e.target.value || null})}
                className="w-full p-2 border rounded bg-white"
              >
                <option value="">Default</option>
                <option value="solid">Solid</option>
                <option value="dashed">Dashed</option>
                <option value="dotted">Dotted</option>
                <option value="double">Double</option>
              </select>
            </div>

            <ColorPicker 
              label="Border Color"
              value={tempStyles.borderColor}
              onChange={(value) => setTempStyles({...tempStyles, borderColor: value})}
            />
          </div>
        </div>

       
      </div>
    )}
  </div>
</TabPanel>

              
              
              {/* Navbar Options Tab */}
              <TabPanel value={activeTab} index={2}>
                {currentMenu.type === 'navbar' && (
                  <div className="bg-gray-100 p-4 rounded">
                    <h3 className="font-medium mb-4">Navbar Configuration</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Navbar Layout</label>
                        <select
                          value={tempNavbarOptions.layout || 'standard'}
                          onChange={(e) => setTempNavbarOptions({...tempNavbarOptions, layout: e.target.value})}
                          className="w-full p-2 border rounded bg-white"
                        >
                          <option value="standard">Standard (Logo left, Menu center, Actions right)</option>
                          <option value="centered">Centered (Logo top, Menu centered)</option>
                          <option value="split">Split (Logo center, Menu on sides)</option>
                          <option value="dynamic">Dynamic  (Responsive layout with scroll effects)</option>                          </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Sticky on Scroll</label>
                        <div className="mt-2">
                          <label className="inline-flex items-center">
                            <input
                              type="checkbox"
                              checked={tempNavbarOptions.stickyOnScroll !== false}
                              onChange={(e) => setTempNavbarOptions({
                                ...tempNavbarOptions, 
                                stickyOnScroll: e.target.checked
                              })}
                              className="mr-2"
                            />
                            Enable sticky navbar on scroll
                          </label>
                        </div>
                      </div>
                      
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Logo</label>
                        <div className="flex items-center space-x-2">
                          {tempNavbarOptions.logo && (
                            <div className="w-12 h-12 border rounded overflow-hidden flex items-center justify-center bg-white relative group">
                              <img 
                                src={tempNavbarOptions.logo} 
                                alt="Navbar logo" 
                                className="max-h-full max-w-full object-contain"
                              />
                              <button
                                type="button" // This explicitly prevents form submission
                                onClick={() => deleteLogo(false)}
                                className="absolute top-0 right-0 bg-red-500 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Delete logo"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                          <div className="flex-1">
                            <input
                              type="file"
                              onChange={(e) => setLogoFile(e.target.files[0])}
                              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                            />
                          </div>
                          <button
                            onClick={() => handleLogoUpload(false)}
                            disabled={!logoFile || uploading}
                            className={`px-2 py-1 ${!logoFile || uploading ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded`}
                          >
                            {uploading ? 'Uploading...' : 'Upload'}
                          </button>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Logo Dimensions</label>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Height</label>
                            <input
                              type="text"
                              value={tempNavbarOptions.logoHeight || '60px'}
                              onChange={(e) => setTempNavbarOptions({...tempNavbarOptions, logoHeight: e.target.value})}
                              placeholder="60px"
                              className="w-full p-2 border rounded"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Width</label>
                            <input
                              type="text"
                              value={tempNavbarOptions.logoWidth || 'auto'}
                              onChange={(e) => setTempNavbarOptions({...tempNavbarOptions, logoWidth: e.target.value})}
                              placeholder="auto"
                              className="w-full p-2 border rounded"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Navbar Height</label>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Desktop</label>
                            <input
                              type="text"
                              value={tempNavbarOptions.heightDesktop || '80px'}
                              onChange={(e) => setTempNavbarOptions({...tempNavbarOptions, heightDesktop: e.target.value})}
                              placeholder="80px"
                              className="w-full p-2 border rounded"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Mobile</label>
                            <input
                              type="text"
                              value={tempNavbarOptions.heightMobile || '70px'}
                              onChange={(e) => setTempNavbarOptions({...tempNavbarOptions, heightMobile: e.target.value})}
                              placeholder="70px"
                              className="w-full p-2 border rounded"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Breakpoint</label>
                        <input
                          type="text"
                          value={tempNavbarOptions.mobileBreakpoint || '768px'}
                          onChange={(e) => setTempNavbarOptions({...tempNavbarOptions, mobileBreakpoint: e.target.value})}
                          placeholder="768px"
                          className="w-full p-2 border rounded"
                        />
                        <p className="text-xs text-gray-500 mt-1">Screen width at which to switch to mobile menu</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Container Max Width</label>
                        <input
                          type="text"
                          value={tempNavbarOptions.containerMaxWidth || '100%'}
                          onChange={(e) => setTempNavbarOptions({...tempNavbarOptions, containerMaxWidth: e.target.value})}
                          placeholder="100%"
                          className="w-full p-2 border rounded"
                        />
                        <p className="text-xs text-gray-500 mt-1">Use values like 100%, 1200px, etc.</p>
                      </div>
 
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Contact Information</label>
                        <div className="flex items-center mb-2">
                          <input
                            type="checkbox"
                            checked={tempNavbarOptions.showContactInfo !== false}
                            onChange={(e) => setTempNavbarOptions({
                              ...tempNavbarOptions, 
                              showContactInfo: e.target.checked
                            })}
                            className="mr-2"
                          />
                          <span>Show contact information in navbar</span>
                        </div>
                        
                        {tempNavbarOptions.showContactInfo && (
                          <div className="mt-2">
                            <label className="block text-xs text-gray-500 mb-1">Position</label>
                            <select
                              value={tempNavbarOptions.contactPosition || 'top'}
                              onChange={(e) => setTempNavbarOptions({...tempNavbarOptions, contactPosition: e.target.value})}
                              className="w-full p-2 border rounded bg-white"
                            >
                              <option value="top">Top bar (above menu)</option>
                              <option value="inline">Inline (with menu items)</option>
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {tempNavbarOptions.layout === 'dynamic' && (
  <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
    <h4 className="text-md font-medium text-gray-800 mb-4">Dynamic Layout Options</h4>
    
    {/* Logo Position */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Logo Position</label>
        <select
          value={tempNavbarOptions.logoPosition || 'left'}
          onChange={(e) => {
            console.log('Logo position changed to:', e.target.value); // Debug log
            setTempNavbarOptions({...tempNavbarOptions, logoPosition: e.target.value});
          }}
          className="w-full p-2 border rounded bg-white"
        >
          <option value="left">Left</option>
          <option value="center">Center</option>
          <option value="right">Right</option>
          <option value="hidden">Hidden</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Menu Position</label>
        <select
          value={tempNavbarOptions.menuPosition || 'center'}
          onChange={(e) => {
            console.log('Menu position changed to:', e.target.value); // Debug log
            setTempNavbarOptions({...tempNavbarOptions, menuPosition: e.target.value});
          }}
          className="w-full p-2 border rounded bg-white"
        >
          <option value="left">Left</option>
          <option value="center">Center</option>
          <option value="right">Right</option>
        </select>
      </div>
    </div>

    {/* Contact Information Settings */}
    <div className="mb-4">
      <div className="flex items-center mb-2">
        <input
          type="checkbox"
          checked={tempNavbarOptions.showContactInDynamic === true}
          onChange={(e) => {
            console.log('Show contact changed to:', e.target.checked); // Debug log
            setTempNavbarOptions({
              ...tempNavbarOptions, 
              showContactInDynamic: e.target.checked
            });
          }}
          className="mr-2"
        />
        <label className="block text-sm font-medium text-gray-700">Show Contact Information</label>
      </div>
      
      {tempNavbarOptions.showContactInDynamic && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 bg-white rounded border">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Contact Position</label>
            <select
              value={tempNavbarOptions.contactPositionDynamic || 'top-right'}
              onChange={(e) => {
                console.log('Contact position changed to:', e.target.value); // Debug log
                setTempNavbarOptions({...tempNavbarOptions, contactPositionDynamic: e.target.value});
              }}
              className="w-full p-2 border rounded bg-white"
            >
              <option value="top-left">Top Left</option>
              <option value="top-center">Top Center</option>
              <option value="top-right">Top Right</option>
              <option value="inline-left">Inline Left</option>
              <option value="inline-right">Inline Right</option>
              <option value="bottom">Bottom Bar</option>
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Contact Display Style</label>
            <select
              value={tempNavbarOptions.contactStyle || 'both'}
              onChange={(e) => {
                console.log('Contact style changed to:', e.target.value); // Debug log
                setTempNavbarOptions({...tempNavbarOptions, contactStyle: e.target.value});
              }}
              className="w-full p-2 border rounded bg-white"
            >
              <option value="phone-only">Phone Only</option>
              <option value="email-only">Email Only</option>
              <option value="both">Both Phone & Email</option>
              <option value="icons-only">Icons Only</option>
            </select>
          </div>
        </div>
      )}
    </div>

    {/* Social Media Settings */}
    <div className="mb-4">
      <div className="flex items-center mb-2">
        <input
          type="checkbox"
          checked={tempNavbarOptions.showSocialInDynamic === true}
          onChange={(e) => {
            console.log('Show social changed to:', e.target.checked); // Debug log
            setTempNavbarOptions({
              ...tempNavbarOptions, 
              showSocialInDynamic: e.target.checked
            });
          }}
          className="mr-2"
        />
        <label className="block text-sm font-medium text-gray-700">Show Social Media Links</label>
      </div>

      {tempNavbarOptions.showSocialInDynamic && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 bg-white rounded border">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Social Media Position</label>
            <select
              value={tempNavbarOptions.socialPosition || 'top-right'}
              onChange={(e) => {
                console.log('Social position changed to:', e.target.value); // Debug log
                setTempNavbarOptions({...tempNavbarOptions, socialPosition: e.target.value});
              }}
              className="w-full p-2 border rounded bg-white"
            >
              <option value="top-left">Top Left</option>
              <option value="top-center">Top Center</option>
              <option value="top-right">Top Right</option>
              <option value="inline-left">Inline Left</option>
              <option value="inline-right">Inline Right</option>
              <option value="bottom">Bottom Bar</option>
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Social Icons Size</label>
            <select
              value={tempNavbarOptions.socialIconSize || 'medium'}
              onChange={(e) => {
                console.log('Social icon size changed to:', e.target.value); // Debug log
                setTempNavbarOptions({...tempNavbarOptions, socialIconSize: e.target.value});
              }}
              className="w-full p-2 border rounded bg-white"
            >
              <option value="small">Small (16px)</option>
              <option value="medium">Medium (20px)</option>
              <option value="large">Large (24px)</option>
            </select>
          </div>

          {/* Add Social Links Management */}
          <div className="col-span-2">
            <label className="block text-xs text-gray-600 mb-2">Social Media Links</label>
            <div className="bg-gray-50 p-3 rounded border">
              {/* Display existing social links */}
              {tempNavbarOptions.socialLinks && tempNavbarOptions.socialLinks.length > 0 ? (
                <div className="mb-3">
                  {tempNavbarOptions.socialLinks.map((social, index) => (
                    <div key={index} className="flex items-center justify-between border rounded p-2 mb-2 bg-white">
                      <div className="flex items-center">
                        <div className="font-medium capitalize">{social.platform}</div>
                        <div className="text-xs text-gray-500 truncate max-w-xs ml-2">{social.url}</div>
                      </div>
                      <button
                        onClick={() => {
                          const updatedLinks = tempNavbarOptions.socialLinks.filter((_, i) => i !== index);
                          setTempNavbarOptions({
                            ...tempNavbarOptions,
                            socialLinks: updatedLinks
                          });
                        }}
                        className="p-1 text-red-500 hover:bg-red-100 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm mb-3">No social links added yet.</p>
              )}

              {/* Simple add new social link */}
              <div className="grid grid-cols-2 gap-2 mb-2">
                <select
                  id="new-social-platform"
                  className="p-2 border rounded bg-white"
                  defaultValue="facebook"
                >
                  <option value="facebook">Facebook</option>
                  <option value="instagram">Instagram</option>
                  <option value="twitter">Twitter</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="youtube">YouTube</option>
                </select>
                <input
                  id="new-social-url"
                  type="text"
                  placeholder="https://..."
                  className="p-2 border rounded"
                />
              </div>
              <button
                onClick={() => {
                  const platform = document.getElementById('new-social-platform').value;
                  const url = document.getElementById('new-social-url').value;
                  if (url) {
                    const newLink = { platform, url };
                    setTempNavbarOptions({
                      ...tempNavbarOptions,
                      socialLinks: [...(tempNavbarOptions.socialLinks || []), newLink]
                    });
                    document.getElementById('new-social-url').value = '';
                  }
                }}
                className="w-full py-2 px-3 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center justify-center"
              >
                <PlusCircle className="w-4 h-4 mr-2" /> Add Social Media Link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

    {/* Scroll Behavior Settings */}
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">Scroll Behavior</label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Logo Behavior on Scroll</label>
          <select
            value={tempNavbarOptions.logoScrollBehavior || 'shrink'}
            onChange={(e) => {
              console.log('Logo scroll behavior changed to:', e.target.value); // Debug log
              setTempNavbarOptions({...tempNavbarOptions, logoScrollBehavior: e.target.value});
            }}
            className="w-full p-2 border rounded bg-white"
          >
            <option value="shrink">Shrink (Scale down)</option>
            <option value="hide">Hide completely</option>
            <option value="fade">Fade opacity</option>
            <option value="none">No change</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-600 mb-1">Menu Behavior on Scroll</label>
          <select
            value={tempNavbarOptions.menuScrollBehavior || 'shrink'}
            onChange={(e) => {
              console.log('Menu scroll behavior changed to:', e.target.value); // Debug log
              setTempNavbarOptions({...tempNavbarOptions, menuScrollBehavior: e.target.value});
            }}
            className="w-full p-2 border rounded bg-white"
          >
            <option value="shrink">Shrink items</option>
            <option value="compress">Compress spacing</option>
            <option value="minimal">Minimal view</option>
            <option value="none">No change</option>
          </select>
        </div>
      </div>
    </div>

    {/* Responsive Breakpoints */}
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">Responsive Settings</label>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Mobile Breakpoint</label>
          <input
            type="text"
            value={tempNavbarOptions.dynamicMobileBreakpoint || '768px'}
            onChange={(e) => {
              console.log('Mobile breakpoint changed to:', e.target.value); // Debug log
              setTempNavbarOptions({...tempNavbarOptions, dynamicMobileBreakpoint: e.target.value});
            }}
            placeholder="768px"
            className="w-full p-2 border rounded"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-600 mb-1">Tablet Breakpoint</label>
          <input
            type="text"
            value={tempNavbarOptions.dynamicTabletBreakpoint || '1024px'}
            onChange={(e) => {
              console.log('Tablet breakpoint changed to:', e.target.value); // Debug log
              setTempNavbarOptions({...tempNavbarOptions, dynamicTabletBreakpoint: e.target.value});
            }}
            placeholder="1024px"
            className="w-full p-2 border rounded"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-600 mb-1">Desktop Breakpoint</label>
          <input
            type="text"
            value={tempNavbarOptions.dynamicDesktopBreakpoint || '1280px'}
            onChange={(e) => {
              console.log('Desktop breakpoint changed to:', e.target.value); // Debug log
              setTempNavbarOptions({...tempNavbarOptions, dynamicDesktopBreakpoint: e.target.value});
            }}
            placeholder="1280px"
            className="w-full p-2 border rounded"
          />
        </div>
      </div>
    </div>

    {/* Animation Settings */}
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">Animation Settings</label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Animation Duration</label>
          <select
            value={tempNavbarOptions.animationDuration || '300ms'}
            onChange={(e) => {
              console.log('Animation duration changed to:', e.target.value); // Debug log
              setTempNavbarOptions({...tempNavbarOptions, animationDuration: e.target.value});
            }}
            className="w-full p-2 border rounded bg-white"
          >
            <option value="150ms">Fast (150ms)</option>
            <option value="300ms">Normal (300ms)</option>
            <option value="500ms">Slow (500ms)</option>
            <option value="700ms">Very Slow (700ms)</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-600 mb-1">Animation Easing</label>
          <select
            value={tempNavbarOptions.animationEasing || 'ease'}
            onChange={(e) => {
              console.log('Animation easing changed to:', e.target.value); // Debug log
              setTempNavbarOptions({...tempNavbarOptions, animationEasing: e.target.value});
            }}
            className="w-full p-2 border rounded bg-white"
          >
            <option value="ease">Ease</option>
            <option value="ease-in">Ease In</option>
            <option value="ease-out">Ease Out</option>
            <option value="ease-in-out">Ease In Out</option>
            <option value="linear">Linear</option>
          </select>
        </div>
      </div>
    </div>

    {/* Advanced Styling Options (Working section from original code) */}
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">Advanced Styling</label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Padding</label>
          <input
            type="text"
            value={tempStyles.padding || ''}
            onChange={(e) => setTempStyles({ ...tempStyles, padding: e.target.value })}
            placeholder="e.g., 0.5rem 1rem"
            className="w-full p-2 border rounded"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-600 mb-1">Margin</label>
          <input
            type="text"
            value={tempStyles.margin || ''}
            onChange={(e) => setTempStyles({ ...tempStyles, margin: e.target.value })}
            placeholder="e.g., 0 auto"
            className="w-full p-2 border rounded"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-600 mb-1">Box Shadow</label>
          <input
            type="text"
            value={tempStyles.boxShadow || ''}
            onChange={(e) => setTempStyles({ ...tempStyles, boxShadow: e.target.value })}
            placeholder="e.g., 0 4px 6px rgba(0,0,0,0.1)"
            className="w-full p-2 border rounded"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-600 mb-1">Transition</label>
          <input
            type="text"
            value={tempStyles.transition || ''}
            onChange={(e) => setTempStyles({ ...tempStyles, transition: e.target.value })}
            placeholder="e.g., all 0.3s ease"
            className="w-full p-2 border rounded"
          />
        </div>
      </div>
    </div>

    {/* Background Effects */}
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">Background Effects</label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="flex items-center mb-2">
            <input
              type="checkbox"
              checked={tempNavbarOptions.enableBlur === true}
              onChange={(e) => {
                console.log('Enable blur changed to:', e.target.checked); // Debug log
                setTempNavbarOptions({
                  ...tempNavbarOptions, 
                  enableBlur: e.target.checked
                });
              }}
              className="mr-2"
            />
            <label className="block text-xs text-gray-600">Enable Backdrop Blur</label>
          </div>
          
          {tempNavbarOptions.enableBlur && (
            <input
              type="text"
              value={tempNavbarOptions.blurAmount || '8px'}
              onChange={(e) => {
                console.log('Blur amount changed to:', e.target.value); // Debug log
                setTempNavbarOptions({...tempNavbarOptions, blurAmount: e.target.value});
              }}
              placeholder="8px"
              className="w-full p-2 border rounded"
            />
          )}
        </div>

        <div>
          <div className="flex items-center mb-2">
            <input
              type="checkbox"
              checked={tempNavbarOptions.enableGradient === true}
              onChange={(e) => {
                console.log('Enable gradient changed to:', e.target.checked); // Debug log
                setTempNavbarOptions({
                  ...tempNavbarOptions, 
                  enableGradient: e.target.checked
                });
              }}
              className="mr-2"
            />
            <label className="block text-xs text-gray-600">Enable Gradient Background</label>
          </div>
          
          {tempNavbarOptions.enableGradient && (
            <input
              type="text"
              value={tempNavbarOptions.gradientValue || 'linear-gradient(90deg, #1F2937, #374151)'}
              onChange={(e) => {
                console.log('Gradient value changed to:', e.target.value); // Debug log
                setTempNavbarOptions({...tempNavbarOptions, gradientValue: e.target.value});
              }}
              placeholder="linear-gradient(90deg, #1F2937, #374151)"
              className="w-full p-2 border rounded text-xs"
            />
          )}
        </div>
      </div>
    </div>

    {/* Search Bar Integration */}
    <div className="mb-4">
      <div className="flex items-center mb-2">
        <input
          type="checkbox"
          checked={tempNavbarOptions.showSearchBar === true}
          onChange={(e) => {
            console.log('Show search bar changed to:', e.target.checked); // Debug log
            setTempNavbarOptions({
              ...tempNavbarOptions, 
              showSearchBar: e.target.checked
            });
          }}
          className="mr-2"
        />
        <label className="block text-sm font-medium text-gray-700">Show Search Bar</label>
      </div>

      {tempNavbarOptions.showSearchBar && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 bg-white rounded border">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Search Position</label>
            <select
              value={tempNavbarOptions.searchPosition || 'center'}
              onChange={(e) => {
                console.log('Search position changed to:', e.target.value); // Debug log
                setTempNavbarOptions({...tempNavbarOptions, searchPosition: e.target.value});
              }}
              className="w-full p-2 border rounded bg-white"
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
              <option value="expand">Expandable</option>
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Search Placeholder</label>
            <input
              type="text"
              value={tempNavbarOptions.searchPlaceholder || 'Search...'}
              onChange={(e) => {
                console.log('Search placeholder changed to:', e.target.value); // Debug log
                setTempNavbarOptions({...tempNavbarOptions, searchPlaceholder: e.target.value});
              }}
              placeholder="Search..."
              className="w-full p-2 border rounded"
            />
          </div>
        </div>
      )}
    </div>

    
  </div>
)}

                    <div className="mt-4">
                      <button
                        onClick={updateMenuStyles}
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                      >
                        Save Navbar Options
                      </button>
                    </div>
                  </div>
                )}
              </TabPanel>
              
              {/* Footer Options Tab */}
              <TabPanel value={activeTab} index={3}>
                {currentMenu.type === 'footer' && (
                  <div className="bg-gray-100 p-4 rounded">
                    <h3 className="font-medium mb-4">Footer Configuration</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Footer Layout</label>
                        <select
                          value={tempFooterOptions.layout || 'standard'}
                          onChange={(e) => setTempFooterOptions({...tempFooterOptions, layout: e.target.value})}
                          className="w-full p-2 border rounded bg-white"
                        >
                          <option value="standard">Standard (Multiple columns)</option>
                          <option value="centered">Centered (Single column)</option>
                          <option value="minimal">Minimal (Copyright only)</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Number of Columns</label>
                        <select
                          value={tempFooterOptions.columns || 4}
                          onChange={(e) => setTempFooterOptions({...tempFooterOptions, columns: parseInt(e.target.value)})}
                          className="w-full p-2 border rounded bg-white"
                          disabled={tempFooterOptions.layout !== 'standard'}
                        >
                          <option value={1}>1 Column</option>
                          <option value={2}>2 Columns</option>
                          <option value={3}>3 Columns</option>
                          <option value={4}>4 Columns</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Logo</label>
                        <div className="flex items-center space-x-2">
                          {tempFooterOptions.logo && (
                            <div className="w-12 h-12 border rounded overflow-hidden flex items-center justify-center bg-white relative group">
                              <img 
                                src={tempFooterOptions.logo} 
                                alt="Footer logo" 
                                className="max-h-full max-w-full object-contain"
                              />
                              <button
                                type="button" // This explicitly prevents form submission
                                onClick={() => deleteLogo(true)}
                                className="absolute top-0 right-0 bg-red-500 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Delete logo"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                          <div className="flex-1">
                            <input
                              type="file"
                              onChange={(e) => setFooterLogoFile(e.target.files[0])}
                              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                            />
                          </div>
                          <button
                            onClick={() => handleLogoUpload(true)}
                            disabled={!footerLogoFile || uploading}
                            className={`px-2 py-1 ${!footerLogoFile || uploading ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded`}
                          >
                            {uploading ? 'Uploading...' : 'Upload'}
                          </button>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Company Info</label>
                        <input
                          type="text"
                          value={tempFooterOptions.companyName || ''}
                          onChange={(e) => setTempFooterOptions({...tempFooterOptions, companyName: e.target.value})}
                          placeholder="Company Name"
                          className="w-full p-2 border rounded mb-2"
                        />
                        <textarea
                          value={tempFooterOptions.companyDescription || ''}
                          onChange={(e) => setTempFooterOptions({...tempFooterOptions, companyDescription: e.target.value})}
                          placeholder="Brief company description"
                          className="w-full p-2 border rounded h-24"
                        />
                      </div>
                      
                      <div className="col-span-1 md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Contact Information</label>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Phone</label>
                            <input
                              type="text"
                              value={tempFooterOptions.contactPhone || ''}
                              onChange={(e) => setTempFooterOptions({...tempFooterOptions, contactPhone: e.target.value})}
                              placeholder="+1 (555) 123-4567"
                              className="w-full p-2 border rounded"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Email</label>
                            <input
                              type="text"
                              value={tempFooterOptions.contactEmail || ''}
                              onChange={(e) => setTempFooterOptions({...tempFooterOptions, contactEmail: e.target.value})}
                              placeholder="info@example.com"
                              className="w-full p-2 border rounded"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Address</label>
                            <input
                              type="text"
                              value={tempFooterOptions.contactAddress || ''}
                              onChange={(e) => setTempFooterOptions({...tempFooterOptions, contactAddress: e.target.value})}
                              placeholder="123 Main St, City, Country"
                              className="w-full p-2 border rounded"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Footer Sections</label>
                        <div className="space-y-2">
                          <label className="inline-flex items-center">
                            <input
                              type="checkbox"
                              checked={tempFooterOptions.showQuickLinks !== false}
                              onChange={(e) => setTempFooterOptions({
                                ...tempFooterOptions, 
                                showQuickLinks: e.target.checked
                              })}
                              className="mr-2"
                            />
                            Show Quick Links
                          </label>
                          <br />
                          <label className="inline-flex items-center">
                            <input
                              type="checkbox"
                              checked={tempFooterOptions.showContactInfo !== false}
                              onChange={(e) => setTempFooterOptions({
                                ...tempFooterOptions, 
                                showContactInfo: e.target.checked
                              })}
                              className="mr-2"
                            />
                            Show Contact Information
                          </label>
                          <br />
                          <label className="inline-flex items-center">
                            <input
                              type="checkbox"
                              checked={tempFooterOptions.showSocialIcons !== false}
                              onChange={(e) => setTempFooterOptions({
                                ...tempFooterOptions, 
                                showSocialIcons: e.target.checked
                              })}
                              className="mr-2"
                            />
                            Show Social Icons
                          </label>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Copyright Text</label>
                        <input
                          type="text"
                          value={tempFooterOptions.copyrightText || '© 2025 Company Name | All Rights Reserved'}
                          onChange={(e) => setTempFooterOptions({...tempFooterOptions, copyrightText: e.target.value})}
                          placeholder="© 2025 Company Name | All Rights Reserved"
                          className="w-full p-2 border rounded"
                        />
                        <p className="text-xs text-gray-500 mt-1">Use YEAR to insert the current year dynamically</p>
                      </div>

                      
                      <div className="col-span-1 md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Social Media Links</label>
                        <div className="bg-gray-50 p-3 rounded border">
                          {/* Display existing social links */}
                          {tempFooterOptions.socialLinks && tempFooterOptions.socialLinks.length > 0 ? (
                            <div className="mb-3">
                              {tempFooterOptions.socialLinks.map((social, index) => (
                                <SocialLinkItem 
                                  key={index}
                                  platform={social.platform}
                                  url={social.url}
                                  onUpdate={(updatedData) => {
                                    const updatedLinks = [...tempFooterOptions.socialLinks];
                                    updatedLinks[index] = updatedData;
                                    setTempFooterOptions({
                                      ...tempFooterOptions,
                                      socialLinks: updatedLinks
                                    });
                                  }}
                                  onRemove={() => {
                                    const updatedLinks = tempFooterOptions.socialLinks.filter((_, i) => i !== index);
                                    setTempFooterOptions({
                                      ...tempFooterOptions,
                                      socialLinks: updatedLinks
                                    });
                                  }}
                                />
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500 text-sm mb-3">No social links added yet.</p>
                          )}

                          {/* Add new social link button */}
                          <button
                            onClick={() => {
                              const newLink = { platform: 'facebook', url: '' };
                              setTempFooterOptions({
                                ...tempFooterOptions,
                                socialLinks: [...(tempFooterOptions.socialLinks || []), newLink]
                              });
                            }}
                            className="w-full py-2 px-3 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center justify-center"
                          >
                            <PlusCircle className="w-4 h-4 mr-2" /> Add Social Media Link
                          </button>
                        </div>
                      </div>
                      
                      <div className="col-span-1 md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Powered By Attribution</label>
                        <div className="flex items-center mb-2">
                          <input
                            type="checkbox"
                            checked={tempFooterOptions.poweredBy?.show !== false}
                            onChange={(e) => setTempFooterOptions({
                              ...tempFooterOptions, 
                              poweredBy: {
                                ...(tempFooterOptions.poweredBy || {}),
                                show: e.target.checked
                              }
                            })}
                            className="mr-2"
                          />
                          <span>Show "Powered By" attribution</span>
                        </div>
                        
                        {tempFooterOptions.poweredBy?.show !== false && (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-3 bg-gray-200 rounded">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Attribution Text</label>
                              <input
                                type="text"
                                value={tempFooterOptions.poweredBy?.text || 'Design & Development By'}
                                onChange={(e) => setTempFooterOptions({
                                  ...tempFooterOptions, 
                                  poweredBy: {
                                    ...(tempFooterOptions.poweredBy || {}),
                                    text: e.target.value
                                  }
                                })}
                                className="w-full p-2 border rounded"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Company Name</label>
                              <input
                                type="text"
                                value={tempFooterOptions.poweredBy?.companyName || 'Your Company'}
                                onChange={(e) => setTempFooterOptions({
                                  ...tempFooterOptions, 
                                  poweredBy: {
                                    ...(tempFooterOptions.poweredBy || {}),
                                    companyName: e.target.value
                                  }
                                })}
                                className="w-full p-2 border rounded"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Company URL</label>
                              <input
                                type="text"
                                value={tempFooterOptions.poweredBy?.companyUrl || '#'}
                                onChange={(e) => setTempFooterOptions({
                                  ...tempFooterOptions, 
                                  poweredBy: {
                                    ...(tempFooterOptions.poweredBy || {}),
                                    companyUrl: e.target.value
                                  }
                                })}
                                className="w-full p-2 border rounded"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <button
                        onClick={updateMenuStyles}
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                      >
                        Save Footer Options
                      </button>
                    </div>
                  </div>
                )}
              </TabPanel>
              
              {/* Custom CSS Tab */}
              <TabPanel value={activeTab} index={4}>
                <div className="bg-gray-100 p-4 rounded">
                  <h3 className="font-medium mb-2">Custom CSS</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Add custom CSS to fine-tune the appearance of your menu. This will be applied to this menu only.
                  </p>
                  <div className="bg-gray-800 rounded-md overflow-hidden">
                    <textarea
                      value={tempCustomCss || ''}
                      onChange={(e) => setTempCustomCss(e.target.value)}
                      className="w-full p-4 h-64 font-mono text-sm bg-gray-800 text-gray-200 border-0 focus:outline-none"
                      placeholder="/* Add your custom CSS here */&#10;&#10;.my-menu-class {&#10;  /* properties */&#10;}"
                    />
                  </div>
                  <div className="mt-4">
                    <button
                      onClick={updateMenuStyles}
                      className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                    >
                      Save Custom CSS
                    </button>
                  </div>
                </div>
              </TabPanel>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MenuManagement;








// / /work perfectly before saba edition
// // components/admin/MenuManagement.js
// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import { 
//   DndContext, 
//   closestCenter, 
//   KeyboardSensor, 
//   PointerSensor, 
//   useSensor, 
//   useSensors 
// } from '@dnd-kit/core';
// import { 
//   arrayMove, 
//   SortableContext, 
//   sortableKeyboardCoordinates, 
//   verticalListSortingStrategy,
//   useSortable
// } from '@dnd-kit/sortable';
// import { CSS } from '@dnd-kit/utilities';
// import { 
//   Home, Package, Mail, Users, Info, ExternalLink, 
//   ShoppingCart, ChevronRight, Trash2, AlertTriangle,
//   Image, Heart, Star, ShoppingBag, User, Code,
//   Settings, FileText, Phone, MessageCircle, Calendar,
//   Map, Bookmark, Award, Database, Layers, Coffee,
//   Gift, Tag, Clock, CreditCard, PenTool, Truck,
//   Linkedin, Twitter, Facebook, Instagram, Youtube,
//   Palette, Layout, Type, Sliders, Eye, EyeOff, Smartphone,
//   Monitor, Copy, Upload, Check, X, Edit3, Save, RefreshCw,
//   PlusCircle, Globe,FacebookIcon, InstagramIcon,LinkedinIcon,
// } from 'lucide-react';

// // Icon mapping
// const iconOptions = [
//   { value: "Home", component: Home },
//   { value: "Package", component: Package },
//   { value: "Mail", component: Mail },
//   { value: "Users", component: Users },
//   { value: "Info", component: Info },
//   { value: "ExternalLink", component: ExternalLink },
//   { value: "ShoppingCart", component: ShoppingCart },
//   { value: "User", component: User },
//   { value: "Settings", component: Settings },
//   { value: "FileText", component: FileText },
//   { value: "Phone", component: Phone },
//   { value: "MessageCircle", component: MessageCircle },
//   { value: "Calendar", component: Calendar },
//   { value: "Map", component: Map },
//   { value: "Bookmark", component: Bookmark },
//   { value: "Award", component: Award },
//   { value: "Database", component: Database },
//   { value: "Layers", component: Layers },
//   { value: "Coffee", component: Coffee },
//   { value: "Gift", component: Gift },
//   { value: "Tag", component: Tag },
//   { value: "Clock", component: Clock },
//   { value: "CreditCard", component: CreditCard },
//   { value: "PenTool", component: PenTool },
//   { value: "Truck", component: Truck },
//   { value: "Image", component: Image },
//   { value: "Heart", component: Heart },
//   { value: "Star", component: Star },
//   { value: "ShoppingBag", component: ShoppingBag },
//   { value: "Linkedin", component: Linkedin },
//   { value: "Twitter", component: Twitter },
//   { value: "Facebook", component: Facebook },
//   { value: "Instagram", component: Instagram },
//   { value: "Youtube", component: Youtube }
// ];

// // Social media platform item component for adding/editing social links
// const SocialLinkItem = ({ platform, url, onUpdate, onRemove }) => {
//   const [editing, setEditing] = useState(false);
//   const [socialData, setSocialData] = useState({ platform, url });

//   const handleSave = () => {
//     onUpdate(socialData);
//     setEditing(false);
//   };

//   if (editing) {
//     return (
//       <div className="border rounded p-3 mb-2 bg-white">
//         <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
//           <div>
//             <label className="block text-xs text-gray-500 mb-1">Platform</label>
//             <select
//               value={socialData.platform}
//               onChange={(e) => setSocialData({...socialData, platform: e.target.value})}
//               className="w-full p-2 border rounded bg-white"
//             >
//               <option value="facebook">Facebook</option>
//               <option value="instagram">Instagram</option>
//               <option value="twitter">Twitter</option>
//               <option value="linkedin">LinkedIn</option>
//               <option value="youtube">YouTube</option>
//             </select>
//           </div>
//           <div className="md:col-span-2">
//             <label className="block text-xs text-gray-500 mb-1">URL</label>
//             <input
//               type="text"
//               value={socialData.url}
//               onChange={(e) => setSocialData({...socialData, url: e.target.value})}
//               placeholder="https://..."
//               className="w-full p-2 border rounded"
//             />
//           </div>
//         </div>
//         <div className="flex justify-end mt-2 space-x-2">
//           <button
//             onClick={handleSave}
//             className="px-2 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600"
//           >
//             Save
//           </button>
//           <button
//             onClick={() => setEditing(false)}
//             className="px-2 py-1 bg-gray-300 text-gray-700 text-sm rounded hover:bg-gray-400"
//           >
//             Cancel
//           </button>
//         </div>
//       </div>
//     );
//   }

//   const SocialIcon = socialIconMap[platform] || Globe;

//   return (
//     <div className="flex items-center justify-between border rounded p-2 mb-2 bg-white">
//       <div className="flex items-center">
//         <SocialIcon className="w-5 h-5 mr-2 text-gray-600" />
//         <div>
//           <div className="font-medium capitalize">{platform}</div>
//           <div className="text-xs text-gray-500 truncate max-w-xs">{url}</div>
//         </div>
//       </div>
//       <div className="flex space-x-1">
//         <button
//           onClick={() => setEditing(true)}
//           className="p-1 text-blue-500 hover:bg-blue-100 rounded"
//         >
//           <Edit3 className="w-4 h-4" />
//         </button>
//         <button
//           onClick={onRemove}
//           className="p-1 text-red-500 hover:bg-red-100 rounded"
//         >
//           <Trash2 className="w-4 h-4" />
//         </button>
//       </div>
//     </div>
//   );
// };

// // Social icon mapping
// const socialIconMap = {
//   facebook: FacebookIcon,
//   instagram: InstagramIcon,
//   twitter: Twitter,
//   linkedin: LinkedinIcon,
//   youtube: Youtube
// };

// // Function to render icon based on name
// const renderIcon = (iconName) => {
//   const icon = iconOptions.find(icon => icon.value === iconName);
//   if (icon) {
//     const IconComponent = icon.component;
//     return <IconComponent className="w-4 h-4 mr-1" />;
//   }
//   return null;
// };

// // Color picker component
// // Color picker component
// const ColorPicker = ({ label, value, onChange, className = "" }) => {
//   return (
//     <div className={`flex items-center ${className}`}>
//       <label className="block text-sm font-medium text-gray-700 mr-2">
//         {label}
//       </label>
//       <div className="flex items-center">
//         <div 
//           className="h-8 w-8 border rounded cursor-pointer"
//           style={{ backgroundColor: value || "#000000" }}
//           onClick={() => {
//             // Focus the color input when the preview is clicked
//             const colorInput = document.getElementById(`color-input-${label.replace(/\s+/g, '-').toLowerCase()}`);
//             if (colorInput) colorInput.click();
//           }}
//         ></div>
//         <input
//           id={`color-input-${label.replace(/\s+/g, '-').toLowerCase()}`}
//           type="color"
//           value={value || "#000000"}
//           onChange={(e) => onChange(e.target.value)}
//           className="h-8 w-8 border rounded cursor-pointer opacity-0 absolute"
//         />
//         <input
//           type="text"
//           value={value || ""}
//           onChange={(e) => onChange(e.target.value)}
//           placeholder="#000000"
//           className="ml-2 p-1 text-sm border rounded w-20"
//         />
//       </div>
//     </div>
//   );
// };

// // Sortable menu item component
// const SortableMenuItem = ({ item, onEdit, onDelete, depth = 0 }) => {
//   const {
//     attributes,
//     listeners,
//     setNodeRef,
//     transform,
//     transition,
//   } = useSortable({ id: item._id });
  
//   const style = {
//     transform: CSS.Transform.toString(transform),
//     transition,
//   };
  
//   return (
//     <div>
//       <div
//         ref={setNodeRef}
//         style={style}
//         className={`p-3 ${depth > 0 ? 'bg-gray-100' : 'bg-gray-50'} border rounded flex justify-between items-center mb-2 ${depth > 0 ? 'ml-6 border-l-4 border-l-blue-300' : ''}`}
//       >
//         <div className="flex items-center">
//           <div 
//             {...attributes} 
//             {...listeners} 
//             className="cursor-move mr-2 text-gray-500"
//           >
//             ☰
//           </div>
//           <div className="flex items-center">
//             {item.icon && renderIcon(item.icon)}
//             <div>
//               <div className="font-medium">{item.title}</div>
//               <div className="text-sm text-gray-500">
//                 {item.path}
//                 {item.isExternal && <ExternalLink className="inline w-3 h-3 ml-1" />}
//               </div>
//               {item.parent && <div className="text-xs text-blue-500 flex items-center mt-1">
//                 <ChevronRight className="w-3 h-3 mr-1" /> Child item
//               </div>}
              
//               {/* Display responsive settings if set */}
//               {(item.mobileOnly || item.desktopOnly) && (
//                 <div className="text-xs text-orange-500 flex items-center mt-1">
//                   {item.mobileOnly && <Smartphone className="w-3 h-3 mr-1" />}
//                   {item.desktopOnly && <Monitor className="w-3 h-3 mr-1" />}
//                   {item.mobileOnly ? 'Mobile only' : 'Desktop only'}
//                 </div>
//               )}
              
//               {/* Display style indicator if item has custom styling */}
//               {item.styles && Object.values(item.styles).some(value => value !== null) && (
//                 <div className="text-xs text-purple-500 flex items-center mt-1">
//                   <Palette className="w-3 h-3 mr-1" /> Custom styled
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>
//         <div className="flex space-x-2">
//           <button 
//             onClick={() => onEdit(item)}
//             className="px-2 py-1 bg-blue-500 text-white rounded text-sm"
//           >
//             Edit
//           </button>
//           <button 
//             onClick={() => onDelete(item._id)}
//             className="px-2 py-1 bg-red-500 text-white rounded text-sm"
//           >
//             Delete
//           </button>
//         </div>
//       </div>
      
//       {/* Render children if they exist */}
//       {item.children && item.children.length > 0 && (
//         <div className="pl-4">
//           {item.children.map(child => (
//             <SortableMenuItem 
//               key={child._id} 
//               item={child} 
//               onEdit={onEdit} 
//               onDelete={onDelete} 
//               depth={depth + 1} 
//             />
//           ))}
//         </div>
//       )}
//     </div>
//   );
// };

// // TabPanel component for styling tabs
// const TabPanel = ({ children, value, index }) => {
//   return (
//     <div className={value === index ? 'block' : 'hidden'}>
//       {children}
//     </div>
//   );
// };

// const MenuManagement = () => {
//   const [menus, setMenus] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [selectedMenu, setSelectedMenu] = useState(null);
//   const [newMenuName, setNewMenuName] = useState('');
//   const [newMenuType, setNewMenuType] = useState('custom');
//   const [editingItem, setEditingItem] = useState(null);
//   const [newMenuItem, setNewMenuItem] = useState({
//     title: '',
//     path: '',
//     parent: '',
//     isExternal: false,
//     icon: '',
//     styles: {},
//     mobileOnly: false,
//     desktopOnly: false,
//     customClasses: ''
//   });
  
//   // Style editing state
//   const [activeTab, setActiveTab] = useState(0);
//   const [previewMode, setPreviewMode] = useState(false);
//   const [tempStyles, setTempStyles] = useState({});
//   const [tempNavbarOptions, setTempNavbarOptions] = useState({});
//   const [tempFooterOptions, setTempFooterOptions] = useState({});
//   const [tempCustomCss, setTempCustomCss] = useState('');
  
//   // File upload state
//   const [logoFile, setLogoFile] = useState(null);
//   const [footerLogoFile, setFooterLogoFile] = useState(null);
//   const [uploading, setUploading] = useState(false);
  
//   const sensors = useSensors(
//     useSensor(PointerSensor),
//     useSensor(KeyboardSensor, {
//       coordinateGetter: sortableKeyboardCoordinates,
//     })
//   );
  
//   useEffect(() => {
//     fetchMenus();
//   }, []);
  
//   // When a menu is selected, initialize temp style states
//   useEffect(() => {
//     if (selectedMenu) {
//       const currentMenu = menus.find(menu => menu._id === selectedMenu);
//       if (currentMenu) {
//         setTempStyles(currentMenu.styles || {});
//         setTempNavbarOptions(currentMenu.navbarOptions || {});
//         setTempFooterOptions(currentMenu.footerOptions || {});
//         setTempCustomCss(currentMenu.customCss || '');
//       }
//     }
//   }, [selectedMenu, menus]);
  
//   const fetchMenus = async () => {
//     try {
//       setLoading(true);
//       const token = localStorage.getItem("token");
//       const response = await axios.get('/api/admin/menus', {
//         headers: {
//           Authorization: `Bearer ${token}`
//         }
//       });
      
//       // Sort all menu items by order before setting state
//       const menusWithItems = response.data.map(menu => {
//         // First sort top-level items
//         const topLevelItems = menu.items.filter(item => !item.parent);
//         const sortedTopLevelItems = sortMenuItemsByOrder(topLevelItems);
        
//         // Sort children for each top-level item
//         sortedTopLevelItems.forEach(item => {
//           if (item.children && item.children.length > 0) {
//             item.children = sortMenuItemsByOrder(item.children);
//           }
//         });
        
//         // Include child items
//         const childItems = menu.items.filter(item => item.parent);
        
//         return {
//           ...menu,
//           items: [...sortedTopLevelItems, ...childItems]
//         };
//       });
      
//       console.log('Fetched and sorted menus:', menusWithItems);
      
//       setMenus(menusWithItems);
//       if (menusWithItems.length > 0 && !selectedMenu) {
//         setSelectedMenu(menusWithItems[0]._id);
//       }
//       setLoading(false);
//     } catch (error) {
//       console.error('Error fetching menus:', error);
//       alert('Failed to load menus. Please try again.');
//     }
//   };
  
  
//   const createMenu = async () => {
//     if (!newMenuName) return;
    
//     try {
//       await axios.post('/api/admin/menus', { 
//         name: newMenuName,
//         type: newMenuType
//       });
//       setNewMenuName('');
//       setNewMenuType('custom');
//       fetchMenus();
//     } catch (error) {
//       console.error('Error creating menu:', error);
//       alert('Failed to create menu. Please try again.');
//     }
//   };
  
//   // Delete entire menu
//   const deleteMenu = async () => {
//     if (!selectedMenu) return;
    
//     if (!window.confirm('Are you sure you want to delete this entire menu? This will delete all items in the menu and cannot be undone.')) {
//       return;
//     }
    
//     try {
//       await axios.delete(`/api/admin/menus/${selectedMenu}`);
//       fetchMenus();
//       setSelectedMenu(null);
//     } catch (error) {
//       console.error('Error deleting menu:', error);
//       alert('Failed to delete menu. Please try again.');
//     }
//   };
  
//   const addMenuItem = async () => {
//     if (!newMenuItem.title || !newMenuItem.path) return;
    
//     try {
//       // Convert empty string parent to null
//       const itemToAdd = {
//         ...newMenuItem,
//         parent: newMenuItem.parent === '' ? null : newMenuItem.parent
//       };
      
//       await axios.post(`/api/admin/menus/${selectedMenu}/items`, itemToAdd);
//       setNewMenuItem({
//         title: '',
//         path: '',
//         parent: '',
//         isExternal: false,
//         icon: '',
//         styles: {},
//         mobileOnly: false,
//         desktopOnly: false,
//         customClasses: ''
//       });
//       fetchMenus();
//     } catch (error) {
//       console.error('Error adding menu item:', error);
//       alert('Failed to add menu item. Please try again.');
//     }
//   };
  
//   const updateMenuItem = async () => {
//     if (!editingItem || !editingItem._id) return;
    
//     try {
//       // Convert empty string parent to null
//       const itemToUpdate = {
//         ...editingItem,
//         parent: editingItem.parent === '' ? null : editingItem.parent
//       };
      
//       await axios.put(`/api/admin/menus/${selectedMenu}/items/${editingItem._id}`, itemToUpdate);
//       setEditingItem(null);
//       fetchMenus();
//     } catch (error) {
//       console.error('Error updating menu item:', error);
//       alert('Failed to update menu item. Please try again.');
//     }
//   };
  
//   // Update menu styles and options
//   const updateMenuStyles = async () => {
//     if (!selectedMenu) return;
    
//     try {
//       const currentMenu = menus.find(menu => menu._id === selectedMenu);
//       if (!currentMenu) return;
      
//       await axios.put(`/api/admin/menus/${selectedMenu}`, {
//         styles: tempStyles,
//         navbarOptions: tempNavbarOptions,
//         footerOptions: tempFooterOptions,
//         customCss: tempCustomCss
//       });
      
//       fetchMenus();
//       alert('Menu styling has been updated successfully!');
//     } catch (error) {
//       console.error('Error updating menu styles:', error);
//       alert('Failed to update menu styles. Please try again.');
//     }
//   };
  
//   // Check if an item is a descendant of another item
//   const isDescendantOf = (potentialParent, item, allItems) => {
//     if (potentialParent._id === item._id) {
//       return true;
//     }
    
//     if (!potentialParent.children || potentialParent.children.length === 0) {
//       return false;
//     }
    
//     return potentialParent.children.some(childId => {
//       const childItem = allItems.find(i => i._id === childId._id || i._id === childId);
//       if (!childItem) return false;
      
//       if (childItem._id === item._id) {
//         return true;
//       }
      
//       return isDescendantOf(childItem, item, allItems);
//     });
//   };

//   const deleteMenuItem = async (itemId) => {
//     if (!window.confirm('Are you sure you want to delete this menu item?')) return;
    
//     try {
//       await axios.delete(`/api/admin/menus/${selectedMenu}/items/${itemId}`);
//       fetchMenus();
//     } catch (error) {
//       console.error('Error deleting menu item:', error);
//       alert('Failed to delete menu item. Please try again.');
//     }
//   };
  
//   // Upload logo file
//   const uploadLogoFile = async (file, isFooter = false) => {
//     if (!file) return null;
    
//     const formData = new FormData();
//     formData.append('file', file);
//     formData.append('type', isFooter ? 'footer-logo' : 'navbar-logo');
    
//     try {
//       setUploading(true);
      
//       const token = localStorage.getItem("token");
//       const response = await axios.post('/api/menus/upload', formData, {
//         headers: {
//           'Content-Type': 'multipart/form-data',
//           'Authorization': `Bearer ${token}`
//         }
//       });
      
//       setUploading(false);
      
//       if (response.data && response.data.fileUrl) {
//         return response.data.fileUrl;
//       } else {
//         throw new Error('Invalid server response');
//       }
//     } catch (error) {
//       console.error('Error uploading logo:', error);
//       setUploading(false);
//       alert('Failed to upload logo: ' + (error.response?.data?.error || error.message));
//       return null;
//     }
//   };  
  
//   // Upload and update logos
//   const handleLogoUpload = async (isFooter = false) => {
//     const file = isFooter ? footerLogoFile : logoFile;
//     if (!file) return;
    
//     const fileUrl = await uploadLogoFile(file, isFooter);
//     if (!fileUrl) return;
    
//     if (isFooter) {
//       setTempFooterOptions({
//         ...tempFooterOptions,
//         logo: fileUrl
//       });
//       setFooterLogoFile(null);
//     } else {
//       setTempNavbarOptions({
//         ...tempNavbarOptions,
//         logo: fileUrl
//       });
//       setLogoFile(null);
//     }
//   };
  
//   // Handle drag end for reordering menu items
//   const handleDragEnd = async (event) => {
//     const { active, over } = event;
    
//     if (active.id !== over.id) {
//       const menu = menus.find(m => m._id === selectedMenu);
//       if (!menu) return;
      
//       const topLevelItems = menu.items.filter(item => !item.parent);
      
//       const oldIndex = topLevelItems.findIndex(item => item._id === active.id);
//       const newIndex = topLevelItems.findIndex(item => item._id === over.id);
      
//       if (oldIndex === -1 || newIndex === -1) {
//         console.warn('Cannot reorder: items not found in top-level items');
//         return;
//       }
      
//       const reorderedTopLevelItems = arrayMove(topLevelItems, oldIndex, newIndex);
      
//       // Update all item orders based on their new positions
//       const orderUpdatePromises = reorderedTopLevelItems.map((item, idx) => {
//         return axios.put(`/api/admin/menus/${selectedMenu}/items/${item._id}/order`, {
//           order: idx
//         });
//       });
      
//       // Update local state first for immediate UI feedback
//       const childItems = menu.items.filter(item => item.parent);
//       const updatedItems = [...reorderedTopLevelItems, ...childItems];
      
//       const updatedMenus = menus.map(m => {
//         if (m._id === selectedMenu) {
//           return {
//             ...m,
//             items: updatedItems
//           };
//         }
//         return m;
//       });
      
//       setMenus(updatedMenus);
      
//       try {
//         // Wait for all order updates to complete
//         await Promise.all(orderUpdatePromises);
        
//         // Refresh data from server to ensure everything is in sync
//         await fetchMenus();
//       } catch (error) {
//         console.error('Error updating menu item orders:', error);
//         alert('Failed to save the new order. Please try again.');
//         // Revert to the original state
//         fetchMenus();
//       }
//     }
//   };



//   const sortMenuItemsByOrder = (items) => {
//     return [...items].sort((a, b) => a.order - b.order);
//   };

  
//   // Function to delete a logo
//   const deleteLogo = async (isFooter = false) => {
//     try {
//       if (!window.confirm('Are you sure you want to delete this logo?')) {
//         return;
//       }
      
//       setUploading(true);
      
//       // Remove the reference from the options
//       if (isFooter) {
//         setTempFooterOptions({
//           ...tempFooterOptions,
//           logo: null
//         });
//       } else {
//         setTempNavbarOptions({
//           ...tempNavbarOptions,
//           logo: null
//         });
//       }
      
//       setUploading(false);
      
//       // Save the changes immediately
//       await updateMenuStyles();
      
//     } catch (error) {
//       console.error('Error deleting logo:', error);
//       setUploading(false);
//       alert('Failed to delete logo. Please try again.');
//     }
//   };
  
//   const currentMenu = menus.find(menu => menu._id === selectedMenu);
  
//   const getMenuTypeLabel = (type) => {
//     switch (type) {
//       case 'navbar': return 'Navigation Bar';
//       case 'footer': return 'Footer';
//       case 'sidebar': return 'Sidebar';
//       case 'custom': return 'Custom Menu';
//       default: return type;
//     }
//   };
  
//   if (loading) {
//     return (
//       <div className="fixed inset-0 flex flex-col items-center justify-center">
//         <img src='../images/loading.gif'/>
//       </div>
//     );
//   }

//   return (
//     <div className="space-y-6">
//       {/* Header */}
//       <h2 className="text-2xl font-semibold">Menu Management</h2>
      
//       <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mx-auto p-4 space-y-6 bg-gray-50 border border-gray-200 rounded-2xl">
//         <div className="md:col-span-1 bg-white p-4 shadow rounded">
//           <h2 className="text-xl font-semibold mb-4">Menus</h2>
          
//           <div className="mb-4">
//             <input
//               type="text"
//               value={newMenuName}
//               onChange={(e) => setNewMenuName(e.target.value)}
//               placeholder="New menu name"
//               className="w-full p-2 border rounded mb-2"
//             />
            
//             <select
//               value={newMenuType}
//               onChange={(e) => setNewMenuType(e.target.value)}
//               className="w-full p-2 border rounded mb-2 bg-white"
//             >
//               <option value="custom">Custom Menu</option>
//               <option value="navbar">Navigation Bar</option>
//               <option value="footer">Footer</option>
//               <option value="sidebar">Sidebar</option>
//             </select>
            
//             <button 
//               onClick={createMenu}
//               className="w-full py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
//             >
//               Create Menu
//             </button>
//           </div>
          
//           <div className="space-y-2">
//             {menus.map(menu => (
//               <button 
//                 key={menu._id}
//                 onClick={() => setSelectedMenu(menu._id)}
//                 className={`w-full p-2 text-left rounded ${
//                   selectedMenu === menu._id ? 'bg-blue-100 border-blue-500' : 'bg-gray-100 hover:bg-gray-200'
//                 }`}
//               >
//                 <div className="flex justify-between items-center">
//                   <span>{menu.name}</span>
//                   <span className="text-xs px-2 py-1 bg-gray-200 rounded-full">{getMenuTypeLabel(menu.type)}</span>
//                 </div>
//               </button>
//             ))}
//           </div>
//           <p className='text-sm text-gray-500 italic mt-4'>Create and customize menus for different sections of your website.</p>
          
//           {selectedMenu && (
//             <button 
//               onClick={deleteMenu}
//               className="w-full mt-6 py-2 bg-red-500 text-white rounded hover:bg-red-600 flex items-center justify-center"
//             >
//               <Trash2 className="w-4 h-4 mr-2" /> Delete Selected Menu
//             </button>
//           )}
//         </div>
        
//         <div className="md:col-span-2 bg-white p-4 shadow rounded">
//           <h2 className="text-xl font-semibold mb-4">
//             {currentMenu ? `Edit ${currentMenu.name} Menu` : 'Select a menu'}
//           </h2>
          
//           {currentMenu && (
//             <>
//               {/* Tabs for different editing aspects */}
//               <div className="mb-6">
//                 <div className="flex border-b">
//                   <button
//                     className={`py-2 px-4 font-medium ${activeTab === 0 ? 'text-blue-500 border-b-2 border-blue-500' : 'text-gray-500'}`}
//                     onClick={() => setActiveTab(0)}
//                   >
//                     Menu Items
//                   </button>
//                   <button
//                     className={`py-2 px-4 font-medium ${activeTab === 1 ? 'text-blue-500 border-b-2 border-blue-500' : 'text-gray-500'}`}
//                     onClick={() => setActiveTab(1)}
//                   >
//                     <Palette className="w-4 h-4 inline mr-1" /> Styling
//                   </button>
                  
//                   {currentMenu.type === 'navbar' && (
//                     <button
//                       className={`py-2 px-4 font-medium ${activeTab === 2 ? 'text-blue-500 border-b-2 border-blue-500' : 'text-gray-500'}`}
//                       onClick={() => setActiveTab(2)}
//                     >
//                       <Layout className="w-4 h-4 inline mr-1" /> Navbar Options
//                     </button>
//                   )}
                  
//                   {currentMenu.type === 'footer' && (
//                     <button
//                       className={`py-2 px-4 font-medium ${activeTab === 3 ? 'text-blue-500 border-b-2 border-blue-500' : 'text-gray-500'}`}
//                       onClick={() => setActiveTab(3)}
//                     >
//                       <Layout className="w-4 h-4 inline mr-1" /> Footer Options
//                     </button>
//                   )}
                  
//                   <button
//                     className={`py-2 px-4 font-medium ${activeTab === 4 ? 'text-blue-500 border-b-2 border-blue-500' : 'text-gray-500'}`}
//                     onClick={() => setActiveTab(4)}
//                   >
//                     <Code className="w-4 h-4 inline mr-1" /> Custom CSS
//                   </button>
//                 </div>
//               </div>
              
//               {/* Menu Items Tab */}
//               <TabPanel value={activeTab} index={0}>
//                 {/* Add new menu item form */}
//                 <div className="mb-6 bg-gray-100 p-4 rounded">
//                   <h3 className="font-medium mb-2">Add Menu Item</h3>
//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                     <input
//                       type="text"
//                       value={newMenuItem.title}
//                       onChange={(e) => setNewMenuItem({...newMenuItem, title: e.target.value})}
//                       placeholder="Title"
//                       className="p-2 border rounded"
//                     />
//                     <input
//                       type="text"
//                       value={newMenuItem.path}
//                       onChange={(e) => setNewMenuItem({...newMenuItem, path: e.target.value})}
//                       placeholder="Path (e.g., /about)"
//                       className="p-2 border rounded"
//                     />
//                   </div>
                  
//                   <div className="mt-4">
//                     <label className="block text-sm font-medium text-gray-700 mb-1">Parent Item</label>
//                     <select
//                       value={newMenuItem.parent}
//                       onChange={(e) => setNewMenuItem({...newMenuItem, parent: e.target.value})}
//                       className="w-full p-2 border rounded bg-white"
//                     >
//                       <option value="">No Parent (Top Level)</option>
//                       {currentMenu.items?.map(item => (
//                         <option key={item._id} value={item._id}>
//                           {item.title} {item.parent ? '(Child Item)' : '(Top Level)'}
//                         </option>
//                       ))}
//                     </select>
//                     <p className="text-xs text-gray-500 mt-1">
//                       {newMenuItem.parent 
//                         ? "This will be a child item that appears in a dropdown menu" 
//                         : "This will be a top-level menu item"}
//                     </p>
//                   </div>
                  
//                   <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1">Icon (Optional)</label>
//                       <select
//                         value={newMenuItem.icon}
//                         onChange={(e) => setNewMenuItem({...newMenuItem, icon: e.target.value})}
//                         className="w-full p-2 border rounded bg-white"
//                       >
//                         <option value="">No Icon</option>
//                         {iconOptions.map(icon => (
//                           <option key={icon.value} value={icon.value}>{icon.value}</option>
//                         ))}
//                       </select>
//                     </div>
                    
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1">Link Type</label>
//                       <div className="flex items-center h-10">
//                         <label className="inline-flex items-center">
//                           <input
//                             type="checkbox"
//                             checked={newMenuItem.isExternal}
//                             onChange={(e) => setNewMenuItem({...newMenuItem, isExternal: e.target.checked})}
//                             className="mr-2"
//                           />
//                           External Link (opens in new tab)
//                         </label>
//                       </div>
//                     </div>
//                   </div>
                  
//                   {/* Responsive display options */}
//                   <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1">Responsive Display</label>
//                       <div className="flex space-x-4">
//                         <label className="inline-flex items-center">
//                           <input
//                             type="checkbox"
//                             checked={newMenuItem.mobileOnly}
//                             onChange={(e) => {
//                               const mobileOnly = e.target.checked;
//                               setNewMenuItem({
//                                 ...newMenuItem, 
//                                 mobileOnly,
//                                 // Can't be both mobile-only and desktop-only
//                                 desktopOnly: mobileOnly ? false : newMenuItem.desktopOnly
//                               });
//                             }}
//                             className="mr-2"
//                           />
//                           <Smartphone className="w-4 h-4 mr-1" /> Mobile Only
//                         </label>
                        
//                         <label className="inline-flex items-center">
//                           <input
//                             type="checkbox"
//                             checked={newMenuItem.desktopOnly}
//                             onChange={(e) => {
//                               const desktopOnly = e.target.checked;
//                               setNewMenuItem({
//                                 ...newMenuItem, 
//                                 desktopOnly,
//                                 // Can't be both mobile-only and desktop-only
//                                 mobileOnly: desktopOnly ? false : newMenuItem.mobileOnly
//                               });
//                             }}
//                             className="mr-2"
//                           />
//                           <Monitor className="w-4 h-4 mr-1" /> Desktop Only
//                         </label>
//                       </div>
//                     </div>
                    
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1">Custom Classes (Optional)</label>
//                       <input
//                         type="text"
//                         value={newMenuItem.customClasses}
//                         onChange={(e) => setNewMenuItem({...newMenuItem, customClasses: e.target.value})}
//                         placeholder="Additional CSS classes"
//                         className="w-full p-2 border rounded"
//                       />
//                     </div>
//                   </div>
                  
//                   {newMenuItem.icon && (
//                     <div className="mt-4 flex items-center">
//                       <span className="mr-2">Preview:</span>
//                       <div className="flex items-center bg-gray-200 px-2 py-1 rounded">
//                         {renderIcon(newMenuItem.icon)}
//                         <span>{newMenuItem.title || 'Item Title'}</span>
//                       </div>
//                     </div>
//                   )}
                  
//                   <button 
//                     onClick={addMenuItem}
//                     className="mt-4 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
//                   >
//                     Add Item
//                   </button>
//                 </div>              
                
//                 {/* Edit menu item form */}
//                 {editingItem && (
//                   <div className="mb-6 bg-yellow-50 border-yellow-200 border p-4 rounded">
//                     <h3 className="font-medium mb-2">Edit Menu Item: {editingItem.title}</h3>
//                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                       <input
//                         type="text"
//                         value={editingItem.title}
//                         onChange={(e) => setEditingItem({...editingItem, title: e.target.value})}
//                         placeholder="Title"
//                         className="p-2 border rounded"
//                       />
//                       <input
//                         type="text"
//                         value={editingItem.path}
//                         onChange={(e) => setEditingItem({...editingItem, path: e.target.value})}
//                         placeholder="Path (e.g., /about)"
//                         className="p-2 border rounded"
//                       />
//                     </div>
                    
//                     <div className="mt-4">
//                       <label className="block text-sm font-medium text-gray-700 mb-1">Parent Item</label>
//                       <select
//                         value={editingItem.parent || ''}
//                         onChange={(e) => setEditingItem({...editingItem, parent: e.target.value || null})}
//                         className="w-full p-2 border rounded bg-white"
//                       >
//                         <option value="">No Parent (Top Level)</option>
//                         {currentMenu.items
//                           ?.filter(item => item._id !== editingItem._id) // Can't be its own parent
//                           .filter(item => !isDescendantOf(item, editingItem, currentMenu.items)) // Can't select own descendants
//                           .map(item => (
//                             <option key={item._id} value={item._id}>
//                               {item.title} {item.parent ? '(Child Item)' : '(Top Level)'}
//                             </option>
//                           ))}
//                       </select>
//                       <p className="text-xs text-gray-500 mt-1">
//                         {editingItem.parent 
//                           ? "This will be a child item that appears in a dropdown menu" 
//                           : "This will be a top-level menu item"}
//                       </p>
//                     </div>
                    
//                     <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Icon (Optional)</label>
//                         <select
//                           value={editingItem.icon || ''}
//                           onChange={(e) => setEditingItem({...editingItem, icon: e.target.value})}
//                           className="w-full p-2 border rounded bg-white"
//                         >
//                           <option value="">No Icon</option>
//                           {iconOptions.map(icon => (
//                             <option key={icon.value} value={icon.value}>{icon.value}</option>
//                           ))}
//                         </select>
//                       </div>
                      
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Link Type</label>
//                         <div className="flex items-center h-10">
//                           <label className="inline-flex items-center">
//                             <input
//                               type="checkbox"
//                               checked={editingItem.isExternal}
//                               onChange={(e) => setEditingItem({...editingItem, isExternal: e.target.checked})}
//                               className="mr-2"
//                             />
//                             External Link (opens in new tab)
//                           </label>
//                         </div>
//                       </div>
//                     </div>
                    
//                     {/* Responsive display options */}
//                     <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Responsive Display</label>
//                         <div className="flex space-x-4">
//                           <label className="inline-flex items-center">
//                             <input
//                               type="checkbox"
//                               checked={editingItem.mobileOnly}
//                               onChange={(e) => {
//                                 const mobileOnly = e.target.checked;
//                                 setEditingItem({
//                                   ...editingItem, 
//                                   mobileOnly,
//                                   // Can't be both mobile-only and desktop-only
//                                   desktopOnly: mobileOnly ? false : editingItem.desktopOnly
//                                 });
//                               }}
//                               className="mr-2"
//                             />
//                             <Smartphone className="w-4 h-4 mr-1" /> Mobile Only
//                           </label>
                          
//                           <label className="inline-flex items-center">
//                             <input
//                               type="checkbox"
//                               checked={editingItem.desktopOnly}
//                               onChange={(e) => {
//                                 const desktopOnly = e.target.checked;
//                                 setEditingItem({
//                                   ...editingItem, 
//                                   desktopOnly,
//                                   // Can't be both mobile-only and desktop-only
//                                   mobileOnly: desktopOnly ? false : editingItem.mobileOnly
//                                 });
//                               }}
//                               className="mr-2"
//                             />
//                             <Monitor className="w-4 h-4 mr-1" /> Desktop Only
//                           </label>
//                         </div>
//                       </div>
                      
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Custom Classes (Optional)</label>
//                         <input
//                           type="text"
//                           value={editingItem.customClasses || ''}
//                           onChange={(e) => setEditingItem({...editingItem, customClasses: e.target.value})}
//                           placeholder="Additional CSS classes"
//                           className="w-full p-2 border rounded"
//                         />
//                       </div>
//                     </div>
                    
//                     {/* Item Styling Section */}
//                     <div className="mt-4">
//                       <div className="flex items-center mb-2">
//                         <label className="block text-sm font-medium text-gray-700">Custom Styling</label>
//                         <button 
//                           type="button"
//                           onClick={() => {
//                             setEditingItem({
//                               ...editingItem,
//                               styles: editingItem.styles ? {} : {
//                                 backgroundColor: null,
//                                 textColor: null,
//                                 hoverTextColor: null,
//                                 hoverBackgroundColor: null
//                               }
//                             });
//                           }}
//                           className="ml-2 text-xs px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded"
//                         >
//                           {editingItem.styles && Object.keys(editingItem.styles).length > 0 
//                             ? 'Clear Styles' 
//                             : 'Add Custom Styling'}
//                         </button>
//                       </div>
                      
//                       {editingItem.styles && Object.keys(editingItem.styles).length > 0 && (
//                         <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 bg-gray-100 rounded">
//                           <ColorPicker 
//                             label="Text Color"
//                             value={editingItem.styles.textColor}
//                             onChange={(value) => setEditingItem({
//                               ...editingItem,
//                               styles: {...editingItem.styles, textColor: value}
//                             })}
//                           />
//                           <ColorPicker 
//                             label="Background Color"
//                             value={editingItem.styles.backgroundColor}
//                             onChange={(value) => setEditingItem({
//                               ...editingItem,
//                               styles: {...editingItem.styles, backgroundColor: value}
//                             })}
//                           />
//                           <ColorPicker 
//                             label="Hover Text Color"
//                             value={editingItem.styles.hoverTextColor}
//                             onChange={(value) => setEditingItem({
//                               ...editingItem,
//                               styles: {...editingItem.styles, hoverTextColor: value}
//                             })}
//                           />
//                           <ColorPicker 
//                             label="Hover Background"
//                             value={editingItem.styles.hoverBackgroundColor}
//                             onChange={(value) => setEditingItem({
//                               ...editingItem,
//                               styles: {...editingItem.styles, hoverBackgroundColor: value}
//                             })}
//                           />
//                         </div>
//                       )}
//                     </div>
                    
//                     {editingItem.icon && (
//                       <div className="mt-4 flex items-center">
//                         <span className="mr-2">Preview:</span>
//                         <div className="flex items-center bg-gray-200 px-2 py-1 rounded">
//                           {renderIcon(editingItem.icon)}
//                           <span>{editingItem.title || 'Item Title'}</span>
//                         </div>
//                       </div>
//                     )}
                    
//                     {/* Warning when changing parent status */}
//                     {(editingItem.parent === null && editingItem.children?.length > 0) && (
//                       <div className="mt-4 p-3 bg-red-100 text-red-700 rounded flex items-start">
//                         <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
//                         <div>
//                           <p className="font-medium">Warning: This item has children</p>
//                           <p className="text-sm">If you change this to be a child item, its existing children will be orphaned.</p>
//                         </div>
//                       </div>
//                     )}
                    
//                     <div className="flex mt-4 space-x-2">
//                       <button 
//                         onClick={updateMenuItem}
//                         className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
//                       >
//                         Update
//                       </button>
//                       <button 
//                         onClick={() => setEditingItem(null)}
//                         className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
//                       >
//                         Cancel
//                       </button>
//                       </div>
//                   </div>
//                 )}
                
//                 {/* Menu items list with drag and drop */}
//                 <div className="menu-items-list">
//                   <h3 className="font-medium mb-2">Menu Items</h3>
                  
//                   {currentMenu.items?.length > 0 ? (
//                     <div>
//                       {/* Only make top-level items sortable */}
//                       <DndContext 
//                         sensors={sensors}
//                         collisionDetection={closestCenter}
//                         onDragEnd={handleDragEnd}
//                       >
//                       <SortableContext 
//                         items={currentMenu.items
//                           .filter(item => !item.parent)
//                           .sort((a, b) => a.order - b.order)
//                           .map(item => item._id)}
//                         strategy={verticalListSortingStrategy}
//                       >
//                         {currentMenu.items
//                           .filter(item => !item.parent)
//                           .sort((a, b) => a.order - b.order)
//                           .map((item) => (
//                             <SortableMenuItem
//                               key={item._id}
//                               item={item}
//                               onEdit={setEditingItem}
//                               onDelete={deleteMenuItem}
//                             />
//                           ))}
//                       </SortableContext>
//                       </DndContext>
//                     </div>
//                   ) : (
//                     <div className="text-center p-4 bg-gray-50 rounded border">
//                       <p className="text-gray-500">No menu items yet. Add your first item above.</p>
//                     </div>
//                   )}
//                 </div>
//               </TabPanel>
              
//               {/* Global Style Tab */}
//               <TabPanel value={activeTab} index={1}>
//                 <div className="bg-gray-100 p-4 rounded">
//                   <div className="flex justify-between mb-4">
//                     <h3 className="font-medium">Global Menu Styling</h3>
//                     <div className="flex items-center">
//                       <button
//                         onClick={() => setPreviewMode(!previewMode)}
//                         className="flex items-center text-sm mr-2 px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded"
//                       >
//                         {previewMode ? (
//                           <>
//                             <Edit3 className="w-3 h-3 mr-1" /> Edit Mode
//                           </>
//                         ) : (
//                           <>
//                             <Eye className="w-3 h-3 mr-1" /> Preview Mode
//                           </>
//                         )}
//                       </button>
//                       <button
//                         onClick={updateMenuStyles}
//                         className="flex items-center text-sm px-2 py-1 bg-green-500 hover:bg-green-600 text-white rounded"
//                       >
//                         <Save className="w-3 h-3 mr-1" /> Save Styles
//                       </button>
//                     </div>
//                   </div>
                  
//                   {previewMode ? (
//                     <div className="p-4 bg-white border rounded">
//                       <h4 className="text-lg mb-2">Style Preview</h4>
//                       <div 
//                         className="mb-4 p-3 rounded overflow-hidden" 
//                         style={{backgroundColor: tempStyles.backgroundColor || '#1F2937'}}
//                       >
//                         <div className="flex space-x-4">
//                           {/* Sample menu items to preview styling */}
//                           {['Home', 'About', 'Services', 'Contact'].map((item, index) => (
//                             <div 
//                               key={index}
//                               className="px-3 py-2 rounded transition-all duration-200 cursor-pointer"
//                               style={{
//                                 color: tempStyles.textColor || '#D1D5DB',
//                                 backgroundColor: index === 1 ? 
//                                   (tempStyles.activeBackgroundColor || '#374151') : 'transparent',
//                                 border: '1px solid transparent'
//                               }}
//                               onMouseEnter={(e) => {
//                                 if (index !== 1) { // Don't apply hover to "active" item
//                                   e.currentTarget.style.color = tempStyles.hoverTextColor || '#93C5FD';
//                                   e.currentTarget.style.backgroundColor = tempStyles.hoverBackgroundColor || '#374151';
//                                 }
//                               }}
//                               onMouseLeave={(e) => {
//                                 if (index !== 1) { // Don't apply hover to "active" item
//                                   e.currentTarget.style.color = tempStyles.textColor || '#D1D5DB';
//                                   e.currentTarget.style.backgroundColor = 'transparent';
//                                 }
//                               }}
//                             >
//                               {item} {index === 1 && <span className="text-xs">(active)</span>}
//                             </div>
//                           ))}
//                         </div>
//                       </div>
//                     </div>
//                   ) : (
//                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                       <ColorPicker 
//                         label="Background Color"
//                         value={tempStyles.backgroundColor}
//                         onChange={(value) => setTempStyles({...tempStyles, backgroundColor: value})}
//                       />
//                       <ColorPicker 
//                         label="Text Color"
//                         value={tempStyles.textColor}
//                         onChange={(value) => setTempStyles({...tempStyles, textColor: value})}
//                       />
//                       <ColorPicker 
//                         label="Hover Text Color"
//                         value={tempStyles.hoverTextColor}
//                         onChange={(value) => setTempStyles({...tempStyles, hoverTextColor: value})}
//                       />
//                       <ColorPicker 
//                         label="Hover Background"
//                         value={tempStyles.hoverBackgroundColor}
//                         onChange={(value) => setTempStyles({...tempStyles, hoverBackgroundColor: value})}
//                       />
//                       <ColorPicker 
//                         label="Active Text Color"
//                         value={tempStyles.activeTextColor}
//                         onChange={(value) => setTempStyles({...tempStyles, activeTextColor: value})}
//                       />
//                       <ColorPicker 
//                         label="Active Background"
//                         value={tempStyles.activeBackgroundColor}
//                         onChange={(value) => setTempStyles({...tempStyles, activeBackgroundColor: value})}
//                       />
                      
//                       {/* Typography options */}
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Font Family</label>
//                         <select
//                           value={tempStyles.fontFamily || ''}
//                           onChange={(e) => setTempStyles({...tempStyles, fontFamily: e.target.value || null})}
//                           className="w-full p-2 border rounded bg-white"
//                         >
//                           <option value="">Default Font</option>
//                           <option value="'Inter', sans-serif">Inter</option>
//                           <option value="'Roboto', sans-serif">Roboto</option>
//                           <option value="'Open Sans', sans-serif">Open Sans</option>
//                           <option value="'Montserrat', sans-serif">Montserrat</option>
//                           <option value="'Lato', sans-serif">Lato</option>
//                           <option value="'Poppins', sans-serif">Poppins</option>
//                           <option value="'Source Sans Pro', sans-serif">Source Sans Pro</option>
//                           <option value="'Oswald', sans-serif">Oswald</option>
//                           <option value="'Roboto Condensed', sans-serif">Roboto Condensed</option>
//                           <option value="'Playfair Display', serif">Playfair Display</option>
//                           <option value="'Merriweather', serif">Merriweather</option>
//                         </select>
//                       </div>
                      
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Font Size</label>
//                         <select
//                           value={tempStyles.fontSize || ''}
//                           onChange={(e) => setTempStyles({...tempStyles, fontSize: e.target.value || null})}
//                           className="w-full p-2 border rounded bg-white"
//                         >
//                           <option value="">Default Size</option>
//                           <option value="0.75rem">Extra Small (12px)</option>
//                           <option value="0.875rem">Small (14px)</option>
//                           <option value="1rem">Medium (16px)</option>
//                           <option value="1.125rem">Large (18px)</option>
//                           <option value="1.25rem">Extra Large (20px)</option>
//                         </select>
//                       </div>
                      
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Font Weight</label>
//                         <select
//                           value={tempStyles.fontWeight || ''}
//                           onChange={(e) => setTempStyles({...tempStyles, fontWeight: e.target.value || null})}
//                           className="w-full p-2 border rounded bg-white"
//                         >
//                           <option value="">Default Weight</option>
//                           <option value="300">Light (300)</option>
//                           <option value="400">Regular (400)</option>
//                           <option value="500">Medium (500)</option>
//                           <option value="600">Semibold (600)</option>
//                           <option value="700">Bold (700)</option>
//                         </select>
//                       </div>
                      
//                       {/* Spacing and Layout */}
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Border Radius</label>
//                         <select
//                           value={tempStyles.borderRadius || ''}
//                           onChange={(e) => setTempStyles({...tempStyles, borderRadius: e.target.value || null})}
//                           className="w-full p-2 border rounded bg-white"
//                         >
//                           <option value="">Default</option>
//                           <option value="0">Square (0px)</option>
//                           <option value="0.125rem">Extra Small (2px)</option>
//                           <option value="0.25rem">Small (4px)</option>
//                           <option value="0.375rem">Medium (6px)</option>
//                           <option value="0.5rem">Large (8px)</option>
//                           <option value="9999px">Pill</option>
//                         </select>
//                       </div>
//                     </div>
//                   )}
//                 </div>
//               </TabPanel>
              
//               {/* Navbar Options Tab */}
//               <TabPanel value={activeTab} index={2}>
//                 {currentMenu.type === 'navbar' && (
//                   <div className="bg-gray-100 p-4 rounded">
//                     <h3 className="font-medium mb-4">Navbar Configuration</h3>
                    
//                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Navbar Layout</label>
//                         <select
//                           value={tempNavbarOptions.layout || 'standard'}
//                           onChange={(e) => setTempNavbarOptions({...tempNavbarOptions, layout: e.target.value})}
//                           className="w-full p-2 border rounded bg-white"
//                         >
//                           <option value="standard">Standard (Logo left, Menu center, Actions right)</option>
//                           <option value="centered">Centered (Logo top, Menu centered)</option>
//                           <option value="split">Split (Logo center, Menu on sides)</option>
//                         </select>
//                       </div>
                      
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Sticky on Scroll</label>
//                         <div className="mt-2">
//                           <label className="inline-flex items-center">
//                             <input
//                               type="checkbox"
//                               checked={tempNavbarOptions.stickyOnScroll !== false}
//                               onChange={(e) => setTempNavbarOptions({
//                                 ...tempNavbarOptions, 
//                                 stickyOnScroll: e.target.checked
//                               })}
//                               className="mr-2"
//                             />
//                             Enable sticky navbar on scroll
//                           </label>
//                         </div>
//                       </div>
                      
                      
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Logo</label>
//                         <div className="flex items-center space-x-2">
//                           {tempNavbarOptions.logo && (
//                             <div className="w-12 h-12 border rounded overflow-hidden flex items-center justify-center bg-white relative group">
//                               <img 
//                                 src={tempNavbarOptions.logo} 
//                                 alt="Navbar logo" 
//                                 className="max-h-full max-w-full object-contain"
//                               />
//                               <button
//                                 type="button" // This explicitly prevents form submission
//                                 onClick={() => deleteLogo(false)}
//                                 className="absolute top-0 right-0 bg-red-500 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
//                                 title="Delete logo"
//                               >
//                                 <X className="w-3 h-3" />
//                               </button>
//                             </div>
//                           )}
//                           <div className="flex-1">
//                             <input
//                               type="file"
//                               onChange={(e) => setLogoFile(e.target.files[0])}
//                               className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
//                             />
//                           </div>
//                           <button
//                             onClick={() => handleLogoUpload(false)}
//                             disabled={!logoFile || uploading}
//                             className={`px-2 py-1 ${!logoFile || uploading ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded`}
//                           >
//                             {uploading ? 'Uploading...' : 'Upload'}
//                           </button>
//                         </div>
//                       </div>
                      
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Logo Dimensions</label>
//                         <div className="grid grid-cols-2 gap-2">
//                           <div>
//                             <label className="block text-xs text-gray-500 mb-1">Height</label>
//                             <input
//                               type="text"
//                               value={tempNavbarOptions.logoHeight || '60px'}
//                               onChange={(e) => setTempNavbarOptions({...tempNavbarOptions, logoHeight: e.target.value})}
//                               placeholder="60px"
//                               className="w-full p-2 border rounded"
//                             />
//                           </div>
//                           <div>
//                             <label className="block text-xs text-gray-500 mb-1">Width</label>
//                             <input
//                               type="text"
//                               value={tempNavbarOptions.logoWidth || 'auto'}
//                               onChange={(e) => setTempNavbarOptions({...tempNavbarOptions, logoWidth: e.target.value})}
//                               placeholder="auto"
//                               className="w-full p-2 border rounded"
//                             />
//                           </div>
//                         </div>
//                       </div>
                      
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Navbar Height</label>
//                         <div className="grid grid-cols-2 gap-2">
//                           <div>
//                             <label className="block text-xs text-gray-500 mb-1">Desktop</label>
//                             <input
//                               type="text"
//                               value={tempNavbarOptions.heightDesktop || '80px'}
//                               onChange={(e) => setTempNavbarOptions({...tempNavbarOptions, heightDesktop: e.target.value})}
//                               placeholder="80px"
//                               className="w-full p-2 border rounded"
//                             />
//                           </div>
//                           <div>
//                             <label className="block text-xs text-gray-500 mb-1">Mobile</label>
//                             <input
//                               type="text"
//                               value={tempNavbarOptions.heightMobile || '70px'}
//                               onChange={(e) => setTempNavbarOptions({...tempNavbarOptions, heightMobile: e.target.value})}
//                               placeholder="70px"
//                               className="w-full p-2 border rounded"
//                             />
//                           </div>
//                         </div>
//                       </div>
                      
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Breakpoint</label>
//                         <input
//                           type="text"
//                           value={tempNavbarOptions.mobileBreakpoint || '768px'}
//                           onChange={(e) => setTempNavbarOptions({...tempNavbarOptions, mobileBreakpoint: e.target.value})}
//                           placeholder="768px"
//                           className="w-full p-2 border rounded"
//                         />
//                         <p className="text-xs text-gray-500 mt-1">Screen width at which to switch to mobile menu</p>
//                       </div>
                      
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Container Max Width</label>
//                         <input
//                           type="text"
//                           value={tempNavbarOptions.containerMaxWidth || '100%'}
//                           onChange={(e) => setTempNavbarOptions({...tempNavbarOptions, containerMaxWidth: e.target.value})}
//                           placeholder="100%"
//                           className="w-full p-2 border rounded"
//                         />
//                         <p className="text-xs text-gray-500 mt-1">Use values like 100%, 1200px, etc.</p>
//                       </div>
 
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Contact Information</label>
//                         <div className="flex items-center mb-2">
//                           <input
//                             type="checkbox"
//                             checked={tempNavbarOptions.showContactInfo !== false}
//                             onChange={(e) => setTempNavbarOptions({
//                               ...tempNavbarOptions, 
//                               showContactInfo: e.target.checked
//                             })}
//                             className="mr-2"
//                           />
//                           <span>Show contact information in navbar</span>
//                         </div>
                        
//                         {tempNavbarOptions.showContactInfo && (
//                           <div className="mt-2">
//                             <label className="block text-xs text-gray-500 mb-1">Position</label>
//                             <select
//                               value={tempNavbarOptions.contactPosition || 'top'}
//                               onChange={(e) => setTempNavbarOptions({...tempNavbarOptions, contactPosition: e.target.value})}
//                               className="w-full p-2 border rounded bg-white"
//                             >
//                               <option value="top">Top bar (above menu)</option>
//                               <option value="inline">Inline (with menu items)</option>
//                             </select>
//                           </div>
//                         )}
//                       </div>
//                     </div>
                    
                    
//                     <div className="mt-4">
//                       <button
//                         onClick={updateMenuStyles}
//                         className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
//                       >
//                         Save Navbar Options
//                       </button>
//                     </div>
//                   </div>
//                 )}
//               </TabPanel>
              
//               {/* Footer Options Tab */}
//               <TabPanel value={activeTab} index={3}>
//                 {currentMenu.type === 'footer' && (
//                   <div className="bg-gray-100 p-4 rounded">
//                     <h3 className="font-medium mb-4">Footer Configuration</h3>
                    
//                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Footer Layout</label>
//                         <select
//                           value={tempFooterOptions.layout || 'standard'}
//                           onChange={(e) => setTempFooterOptions({...tempFooterOptions, layout: e.target.value})}
//                           className="w-full p-2 border rounded bg-white"
//                         >
//                           <option value="standard">Standard (Multiple columns)</option>
//                           <option value="centered">Centered (Single column)</option>
//                           <option value="minimal">Minimal (Copyright only)</option>
//                         </select>
//                       </div>
                      
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Number of Columns</label>
//                         <select
//                           value={tempFooterOptions.columns || 4}
//                           onChange={(e) => setTempFooterOptions({...tempFooterOptions, columns: parseInt(e.target.value)})}
//                           className="w-full p-2 border rounded bg-white"
//                           disabled={tempFooterOptions.layout !== 'standard'}
//                         >
//                           <option value={1}>1 Column</option>
//                           <option value={2}>2 Columns</option>
//                           <option value={3}>3 Columns</option>
//                           <option value={4}>4 Columns</option>
//                         </select>
//                       </div>
                      
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Logo</label>
//                         <div className="flex items-center space-x-2">
//                           {tempFooterOptions.logo && (
//                             <div className="w-12 h-12 border rounded overflow-hidden flex items-center justify-center bg-white relative group">
//                               <img 
//                                 src={tempFooterOptions.logo} 
//                                 alt="Footer logo" 
//                                 className="max-h-full max-w-full object-contain"
//                               />
//                               <button
//                                 type="button" // This explicitly prevents form submission
//                                 onClick={() => deleteLogo(true)}
//                                 className="absolute top-0 right-0 bg-red-500 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
//                                 title="Delete logo"
//                               >
//                                 <X className="w-3 h-3" />
//                               </button>
//                             </div>
//                           )}
//                           <div className="flex-1">
//                             <input
//                               type="file"
//                               onChange={(e) => setFooterLogoFile(e.target.files[0])}
//                               className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
//                             />
//                           </div>
//                           <button
//                             onClick={() => handleLogoUpload(true)}
//                             disabled={!footerLogoFile || uploading}
//                             className={`px-2 py-1 ${!footerLogoFile || uploading ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded`}
//                           >
//                             {uploading ? 'Uploading...' : 'Upload'}
//                           </button>
//                         </div>
//                       </div>
                      
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Company Info</label>
//                         <input
//                           type="text"
//                           value={tempFooterOptions.companyName || ''}
//                           onChange={(e) => setTempFooterOptions({...tempFooterOptions, companyName: e.target.value})}
//                           placeholder="Company Name"
//                           className="w-full p-2 border rounded mb-2"
//                         />
//                         <textarea
//                           value={tempFooterOptions.companyDescription || ''}
//                           onChange={(e) => setTempFooterOptions({...tempFooterOptions, companyDescription: e.target.value})}
//                           placeholder="Brief company description"
//                           className="w-full p-2 border rounded h-24"
//                         />
//                       </div>
                      
//                       <div className="col-span-1 md:col-span-2">
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Contact Information</label>
//                         <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//                           <div>
//                             <label className="block text-xs text-gray-500 mb-1">Phone</label>
//                             <input
//                               type="text"
//                               value={tempFooterOptions.contactPhone || ''}
//                               onChange={(e) => setTempFooterOptions({...tempFooterOptions, contactPhone: e.target.value})}
//                               placeholder="+1 (555) 123-4567"
//                               className="w-full p-2 border rounded"
//                             />
//                           </div>
//                           <div>
//                             <label className="block text-xs text-gray-500 mb-1">Email</label>
//                             <input
//                               type="text"
//                               value={tempFooterOptions.contactEmail || ''}
//                               onChange={(e) => setTempFooterOptions({...tempFooterOptions, contactEmail: e.target.value})}
//                               placeholder="info@example.com"
//                               className="w-full p-2 border rounded"
//                             />
//                           </div>
//                           <div>
//                             <label className="block text-xs text-gray-500 mb-1">Address</label>
//                             <input
//                               type="text"
//                               value={tempFooterOptions.contactAddress || ''}
//                               onChange={(e) => setTempFooterOptions({...tempFooterOptions, contactAddress: e.target.value})}
//                               placeholder="123 Main St, City, Country"
//                               className="w-full p-2 border rounded"
//                             />
//                           </div>
//                         </div>
//                       </div>
                      
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Footer Sections</label>
//                         <div className="space-y-2">
//                           <label className="inline-flex items-center">
//                             <input
//                               type="checkbox"
//                               checked={tempFooterOptions.showQuickLinks !== false}
//                               onChange={(e) => setTempFooterOptions({
//                                 ...tempFooterOptions, 
//                                 showQuickLinks: e.target.checked
//                               })}
//                               className="mr-2"
//                             />
//                             Show Quick Links
//                           </label>
//                           <br />
//                           <label className="inline-flex items-center">
//                             <input
//                               type="checkbox"
//                               checked={tempFooterOptions.showContactInfo !== false}
//                               onChange={(e) => setTempFooterOptions({
//                                 ...tempFooterOptions, 
//                                 showContactInfo: e.target.checked
//                               })}
//                               className="mr-2"
//                             />
//                             Show Contact Information
//                           </label>
//                           <br />
//                           <label className="inline-flex items-center">
//                             <input
//                               type="checkbox"
//                               checked={tempFooterOptions.showSocialIcons !== false}
//                               onChange={(e) => setTempFooterOptions({
//                                 ...tempFooterOptions, 
//                                 showSocialIcons: e.target.checked
//                               })}
//                               className="mr-2"
//                             />
//                             Show Social Icons
//                           </label>
//                         </div>
//                       </div>
                      
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Copyright Text</label>
//                         <input
//                           type="text"
//                           value={tempFooterOptions.copyrightText || '© 2025 Company Name | All Rights Reserved'}
//                           onChange={(e) => setTempFooterOptions({...tempFooterOptions, copyrightText: e.target.value})}
//                           placeholder="© 2025 Company Name | All Rights Reserved"
//                           className="w-full p-2 border rounded"
//                         />
//                         <p className="text-xs text-gray-500 mt-1">Use YEAR to insert the current year dynamically</p>
//                       </div>

                      
//                       <div className="col-span-1 md:col-span-2">
//                         <label className="block text-sm font-medium text-gray-700 mb-2">Social Media Links</label>
//                         <div className="bg-gray-50 p-3 rounded border">
//                           {/* Display existing social links */}
//                           {tempFooterOptions.socialLinks && tempFooterOptions.socialLinks.length > 0 ? (
//                             <div className="mb-3">
//                               {tempFooterOptions.socialLinks.map((social, index) => (
//                                 <SocialLinkItem 
//                                   key={index}
//                                   platform={social.platform}
//                                   url={social.url}
//                                   onUpdate={(updatedData) => {
//                                     const updatedLinks = [...tempFooterOptions.socialLinks];
//                                     updatedLinks[index] = updatedData;
//                                     setTempFooterOptions({
//                                       ...tempFooterOptions,
//                                       socialLinks: updatedLinks
//                                     });
//                                   }}
//                                   onRemove={() => {
//                                     const updatedLinks = tempFooterOptions.socialLinks.filter((_, i) => i !== index);
//                                     setTempFooterOptions({
//                                       ...tempFooterOptions,
//                                       socialLinks: updatedLinks
//                                     });
//                                   }}
//                                 />
//                               ))}
//                             </div>
//                           ) : (
//                             <p className="text-gray-500 text-sm mb-3">No social links added yet.</p>
//                           )}

//                           {/* Add new social link button */}
//                           <button
//                             onClick={() => {
//                               const newLink = { platform: 'facebook', url: '' };
//                               setTempFooterOptions({
//                                 ...tempFooterOptions,
//                                 socialLinks: [...(tempFooterOptions.socialLinks || []), newLink]
//                               });
//                             }}
//                             className="w-full py-2 px-3 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center justify-center"
//                           >
//                             <PlusCircle className="w-4 h-4 mr-2" /> Add Social Media Link
//                           </button>
//                         </div>
//                       </div>
                      
//                       <div className="col-span-1 md:col-span-2">
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Powered By Attribution</label>
//                         <div className="flex items-center mb-2">
//                           <input
//                             type="checkbox"
//                             checked={tempFooterOptions.poweredBy?.show !== false}
//                             onChange={(e) => setTempFooterOptions({
//                               ...tempFooterOptions, 
//                               poweredBy: {
//                                 ...(tempFooterOptions.poweredBy || {}),
//                                 show: e.target.checked
//                               }
//                             })}
//                             className="mr-2"
//                           />
//                           <span>Show "Powered By" attribution</span>
//                         </div>
                        
//                         {tempFooterOptions.poweredBy?.show !== false && (
//                           <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-3 bg-gray-200 rounded">
//                             <div>
//                               <label className="block text-xs text-gray-600 mb-1">Attribution Text</label>
//                               <input
//                                 type="text"
//                                 value={tempFooterOptions.poweredBy?.text || 'Design & Development By'}
//                                 onChange={(e) => setTempFooterOptions({
//                                   ...tempFooterOptions, 
//                                   poweredBy: {
//                                     ...(tempFooterOptions.poweredBy || {}),
//                                     text: e.target.value
//                                   }
//                                 })}
//                                 className="w-full p-2 border rounded"
//                               />
//                             </div>
//                             <div>
//                               <label className="block text-xs text-gray-600 mb-1">Company Name</label>
//                               <input
//                                 type="text"
//                                 value={tempFooterOptions.poweredBy?.companyName || 'Your Company'}
//                                 onChange={(e) => setTempFooterOptions({
//                                   ...tempFooterOptions, 
//                                   poweredBy: {
//                                     ...(tempFooterOptions.poweredBy || {}),
//                                     companyName: e.target.value
//                                   }
//                                 })}
//                                 className="w-full p-2 border rounded"
//                               />
//                             </div>
//                             <div>
//                               <label className="block text-xs text-gray-600 mb-1">Company URL</label>
//                               <input
//                                 type="text"
//                                 value={tempFooterOptions.poweredBy?.companyUrl || '#'}
//                                 onChange={(e) => setTempFooterOptions({
//                                   ...tempFooterOptions, 
//                                   poweredBy: {
//                                     ...(tempFooterOptions.poweredBy || {}),
//                                     companyUrl: e.target.value
//                                   }
//                                 })}
//                                 className="w-full p-2 border rounded"
//                               />
//                             </div>
//                           </div>
//                         )}
//                       </div>
//                     </div>
                    
//                     <div className="mt-4">
//                       <button
//                         onClick={updateMenuStyles}
//                         className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
//                       >
//                         Save Footer Options
//                       </button>
//                     </div>
//                   </div>
//                 )}
//               </TabPanel>
              
//               {/* Custom CSS Tab */}
//               <TabPanel value={activeTab} index={4}>
//                 <div className="bg-gray-100 p-4 rounded">
//                   <h3 className="font-medium mb-2">Custom CSS</h3>
//                   <p className="text-sm text-gray-600 mb-4">
//                     Add custom CSS to fine-tune the appearance of your menu. This will be applied to this menu only.
//                   </p>
//                   <div className="bg-gray-800 rounded-md overflow-hidden">
//                     <textarea
//                       value={tempCustomCss || ''}
//                       onChange={(e) => setTempCustomCss(e.target.value)}
//                       className="w-full p-4 h-64 font-mono text-sm bg-gray-800 text-gray-200 border-0 focus:outline-none"
//                       placeholder="/* Add your custom CSS here */&#10;&#10;.my-menu-class {&#10;  /* properties */&#10;}"
//                     />
//                   </div>
//                   <div className="mt-4">
//                     <button
//                       onClick={updateMenuStyles}
//                       className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
//                     >
//                       Save Custom CSS
//                     </button>
//                   </div>
//                 </div>
//               </TabPanel>
//             </>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default MenuManagement;
